/*
 * Co-Pilot Chat Assistant - Office Add-in
 * Interactive chat functionality for ms project applications
 */

/* global console document, Office, window */

import { chatService, DEFAULT_EDIT_HARNESS_MODE } from "../services/chatService.js";
import { buildStalePlanNotice } from "./stalePlanNotice.js";
import { createUidNoticeTracker } from "./uidNotice.js";
import { serverErrorMessage } from "./errorText.js";
import { buildRatingControls, mountProblemReport } from "./feedbackControls.js";
import { healthAPI, filesAPI, modelsAPI, chatAPI, projectsAPI } from "../services/api.js";
import { buildPendingSyncPayload } from "../services/pendingSync.js";
import { msProjectService } from "../services/msProjectService.js";
import { projectWatcher } from "../services/projectWatcher.js";
import { SessionSelector } from "./components/sessionSelector.js";
import { classifyMessageForRender, extractDownloadInfo, extractToolCallSummary, isNoChangesSync, staleSyncNotice } from "./messageRenderDecisions.js";
import { buildCoarseSummaryLine, buildDetailLines } from "./toolCallSummaryDisplay.js";
import { downloadTableAsCsv } from "./tableCsvExport.js";
import { createActivityLog } from "./activityFeed.js";
import { createActivityStripView } from "./activityStripView.js";
import { formatUsageRows, formatSubscriptionPeriod } from "./usageDisplay.js";
import {
  initSentry,
  payloadSnapshot,
  sentryCaptureException,
  setSentryChatContext,
  setSentryScope,
} from "../services/sentry.js";
import { viewManagementService } from "../services/viewManagementService.js";
import { APIAlignmentTest } from "../services/apiAlignmentTest.js";
import { API_CONFIG, ERROR_MESSAGES } from "../config/api.js";
import { getModelTier, setModelTier } from "../utils/modelTierStore.js";
import { createStreamSessionGuard } from "./streamSessionGuard.js";
import { takeRouteOverride } from "./routeSelector.js";
import { enhanceSelect } from "./listboxSelect.js";
import { fitMenuToViewport } from "./menuPlacement.js";
import {
  loadAuthConfig,
  recoverWebSession,
  signInWithOfficeDialog,
  validateLegacyApiKey,
} from "../auth/entraClient.js";
import {
  clearLegacyApiKey,
  clearWebSession,
  getAuthConfig,
  getCurrentUser,
  getLegacyApiKey,
  getRequestAuthHeaders,
  initializeLegacyCredentialMigration,
  setLegacyApiKey,
} from "../auth/session.js";
import {
  createTermsPanel,
  termsAcceptanceRequired,
  termsWereUpdated,
} from "../auth/termsAcceptance.js";
import {
  approveDiscoveredConnectorPairing,
  approveConnectorPairing,
  denyConnectorPairing,
  dismissDiscoveredConnectorPairing,
  listConnectorInstallations,
} from "../auth/connectorPairingClient.js";
import { connectorGateApplies, shouldGateChat } from "../auth/connectorGate.js";
import { ConnectorDiscoveryController } from "../auth/connectorDiscoveryController.js";
import { applyComposerLayout } from "./composerLayout.js";
import {
  createAttachmentUploader,
  createPendingEntry,
  checkPickAgainstLimit,
  stillReadingNotice,
  STATUS as ATTACHMENT_STATUS,
} from "./attachmentUploads.js";
import { buildAttachedChipsHtml } from "./attachmentChips.js";
import { marked } from "marked";


console.log("Office object available:", typeof Office !== "undefined");
console.log("Office.context available:", typeof Office?.context !== "undefined");
console.log("Office.HostType available:", typeof Office?.HostType !== "undefined");
console.log("Office.HostType.Project:", Office?.HostType?.Project);

let chatHistory = [];
let isTyping = false;
let isInitialized = false;
let isUploadingComparisonFile = false;
let isStartingApp = false;
let accountStateGeneration = 0;
let comparisonUploadAbortController = null;
let chatUiHandlersBound = false;
let authSetupHandlersBound = false;
let accountControlsBound = false;
let sessionSelectorMounted = false;
let sessionSelectorInstance = null;
let projectWatcherWired = false;
let sessionHistoryWired = false;
let currentDiscoveryCandidate = null;
// Set when the backend reports the connector belongs to another account AFTER we
// already tried without confirmation (ownership can change between the heartbeat
// that rendered the prompt and the click). Arms an explicit second Connect press
// instead of silently replacing the other account's connector.
let forcedReassignmentPairingId = "";
const MAX_FILTER_FLAGS = 20;
const MAX_COMPARE_UPLOAD_SIZE = 100 * 1024 * 1024;
const SUPPORTED_COMPARE_EXTENSIONS = new Set(["xml", "mpp"]);
const usedFilterFlagFields = new Set();
let nextFilterFlagIndex = 1;

const connectorDiscoveryController = new ConnectorDiscoveryController({
  onCandidates: renderConnectorDiscoveryCandidates,
  onCompleted: handleConnectorDiscoveryCompleted,
  onError: (error) => console.warn("Connector discovery heartbeat failed:", error),
});

initSentry();
setSentryScope({
  subsystem: "taskpane",
  feature: "startup",
  host: "office_addin",
});

function normalizeFlagField(flagField) {
  if (!flagField || typeof flagField !== "string") return null;
  const match = flagField.trim().match(/^flag(\d{1,2})$/i);
  if (!match) return null;

  const flagNumber = Number(match[1]);
  if (Number.isNaN(flagNumber) || flagNumber < 1 || flagNumber > MAX_FILTER_FLAGS) {
    return null;
  }

  return `Flag${flagNumber}`;
}

function getPlannedTargetFlagField() {
  if (nextFilterFlagIndex < 1 || nextFilterFlagIndex > MAX_FILTER_FLAGS) {
    return null;
  }
  return `Flag${nextFilterFlagIndex}`;
}

function isAffirmativeFlagValue(value) {
  if (value === true || value === 1 || value === "1") return true;
  if (typeof value === "string" && value.trim().toLowerCase() === "yes") return true;
  return false;
}

function setAuthStatus(message, type = "") {
  const statusEl = document.getElementById("authStatus");
  statusEl.textContent = message || "";
  statusEl.className = `api-key-status ${type}`.trim();
}

function showAuthSetup(runtimeConfig = getAuthConfig()) {
  const setupEl = document.getElementById("auth-setup");
  const appBody = document.getElementById("app-body");
  setupEl.style.display = "flex";
  appBody.style.display = "none";
  termsGate?.hide();
  termsGateResume = null;

  const input = document.getElementById("legacyApiKeyInput");
  const signInBtn = document.getElementById("entraSignInBtn");
  const legacyPanel = document.getElementById("legacyAuthPanel");
  signInBtn.style.display = runtimeConfig?.entra?.enabled ? "block" : "none";
  legacyPanel.style.display = runtimeConfig?.legacy_linking_enabled || runtimeConfig?.mode === "api_key"
    ? "block"
    : "none";

  if (!authSetupHandlersBound) {
    signInBtn.addEventListener("click", async () => {
      signInBtn.disabled = true;
      setAuthStatus("Opening Microsoft sign-in...");
      try {
        const session = await signInWithOfficeDialog("signin");
        await continueAfterSignIn(session, runtimeConfig);
      } catch (error) {
        setAuthStatus(error.message || "Sign-in failed", "error");
      } finally {
        signInBtn.disabled = false;
      }
    });

    document.getElementById("legacyContinueBtn").addEventListener("click", async () => {
      const key = input.value.trim();
      if (!key) {
        setAuthStatus("Enter the legacy API key.", "error");
        return;
      }
      setAuthStatus("Validating...");
      try {
        await validateLegacyApiKey(key);
        setLegacyApiKey(key);
        setupEl.style.display = "none";
        await startApp();
      } catch (error) {
        clearLegacyApiKey();
        setAuthStatus(error.message || "Legacy API key is invalid", "error");
      }
    });

    authSetupHandlersBound = true;
  }
}

let termsGate = null;
// What accepting leads to. After a sign-in that is startApp(); when the server
// refuses a call mid-session it is just showing the chat again, untouched.
let termsGateResume = null;
let termsPromptPending = false;

/**
 * Acceptance of the terms sits between an Entra sign-in and startApp(). The
 * backend says whether it is needed (`session.terms.acceptance_required`); a
 * legacy API-key session has no web session and never comes through here.
 * Wired lazily and once, like the auth-setup handlers.
 */
function termsGateController() {
  if (termsGate) return termsGate;
  termsGate = createTermsPanel(document.getElementById("terms-gate"), {
    onAccepted: async () => {
      termsGate.hide();
      const resume = termsGateResume || startApp;
      termsGateResume = null;
      await resume();
    },
    onDeclined: async () => {
      termsGateResume = null;
      // Declining means not using the service, so the session ends -- the same
      // steps as the account panel's "Sign out", without the panel.
      try {
        await fetch(`${API_CONFIG.baseURL}/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: getRequestAuthHeaders("POST"),
        });
      } catch (_) {
        // The local sign-out below is what matters; an orphaned server
        // session expires on its own.
      }
      clearWebSession();
      resetUserSpecificClientState();
      showAuthSetup();
      setAuthStatus("You were signed out. Sign in again whenever you are ready to continue.");
    },
  });
  return termsGate;
}

/**
 * Where an Entra session goes after sign-in or recovery: the acceptance screen
 * if the backend requires it, otherwise straight into the app.
 */
async function continueAfterSignIn(session, runtimeConfig = getAuthConfig()) {
  document.getElementById("auth-setup").style.display = "none";
  if (termsAcceptanceRequired(session, runtimeConfig)) {
    document.getElementById("app-body").style.display = "none";
    termsGateResume = null;
    termsGateController().show(runtimeConfig, { updated: termsWereUpdated(session) });
    return;
  }
  await startApp();
}

/**
 * The server refused a call because the terms changed while the pane was open.
 * The session is still good, so nothing is reset: the chat is hidden (not
 * cleared) behind the acceptance screen and comes back exactly as it was once
 * the person accepts. The runtime is fetched again first, because the version
 * this pane loaded at start is the one the server just stopped accepting.
 */
async function showTermsOverChat() {
  if (termsPromptPending || termsGate?.isShowing()) return;
  const appBody = document.getElementById("app-body");
  if (!getCurrentUser() || appBody.style.display === "none") return;
  termsPromptPending = true;
  try {
    let runtimeConfig = getAuthConfig();
    try {
      runtimeConfig = await loadAuthConfig();
    } catch (error) {
      console.warn("Could not refresh the terms before showing them:", error);
    }
    if (!runtimeConfig?.terms?.version || !getCurrentUser()) return;
    appBody.style.display = "none";
    termsGateResume = async () => {
      appBody.style.display = "flex";
    };
    // They were already using the app, so they accepted an earlier version.
    termsGateController().show(runtimeConfig, { updated: true });
  } finally {
    termsPromptPending = false;
  }
}

window.addEventListener("terms-acceptance-required", () => {
  showTermsOverChat();
});

async function initializeAuthentication() {
  try {
    initializeLegacyCredentialMigration();
    const runtimeConfig = await loadAuthConfig();
    if (runtimeConfig.mode === "disabled") {
      await startApp();
      return;
    }
    if (runtimeConfig.entra?.enabled) {
      const session = await recoverWebSession();
      if (session) {
        await continueAfterSignIn(session, runtimeConfig);
        return;
      }
    }
    const legacyKey = getLegacyApiKey();
    if (legacyKey && ["api_key", "dual"].includes(runtimeConfig.mode)) {
      try {
        await validateLegacyApiKey(legacyKey);
        await startApp();
        return;
      } catch (_) {
        clearLegacyApiKey();
      }
    }
    showAuthSetup(runtimeConfig);
  } catch (error) {
    console.error("Authentication initialization failed:", error);
    showAuthSetup(getAuthConfig());
    setAuthStatus(error.message || "Cannot reach the authentication service", "error");
  }
}

function hideConnectorDiscoveryPrompt() {
  currentDiscoveryCandidate = null;
  const prompt = document.getElementById("connectorDiscoveryPrompt");
  if (prompt) prompt.hidden = true;
}

function renderConnectorDiscoveryCandidates(candidates) {
  const prompt = document.getElementById("connectorDiscoveryPrompt");
  if (!prompt) return;
  const candidate = Array.isArray(candidates) ? candidates[0] : null;
  if (!candidate) {
    hideConnectorDiscoveryPrompt();
    return;
  }
  currentDiscoveryCandidate = candidate;
  const approved = candidate.status === "approved";
  document.getElementById("connectorDiscoveryTitle").textContent = approved
    ? "Finishing pairing..."
    : "Pair Microsoft Project?";
  document.getElementById("connectorDiscoveryDescription").textContent = approved
    ? "Microsoft Project is completing the secure pairing."
    : candidate.ownership_state === "other_user"
      ? "Pairing will replace this connector's previous account."
      : "Confirm that this code matches Microsoft Project.";
  document.getElementById("connectorDiscoveryCode").textContent =
    candidate.user_code || "";
  document.getElementById("connectDiscoveredConnectorBtn").disabled = approved;
  document.getElementById("dismissDiscoveredConnectorBtn").disabled = approved;
  prompt.hidden = false;
}

/**
 * Chat is gated until this account has a paired connector, so the user cannot
 * reach a half-working state where chat answers but ribbon Sync silently does
 * nothing.
 *
 * Only Entra web sessions are gated. A legacy API-key session has no connector
 * installations to list, so gating one would lock it out permanently.
 */
function connectorGateRequired() {
  return connectorGateApplies({
    authConfig: getAuthConfig(),
    currentUser: getCurrentUser(),
    legacyApiKey: getLegacyApiKey(),
  });
}

/**
 * Hides the chat, not the app. Everything behind it keeps running — in
 * particular the project watcher, whose identity feeds the discovery binding
 * hashes that surface the pairing request in the first place. Gating any
 * earlier would strand the user on a screen that can never resolve itself.
 */
function applyConnectorGate(paired) {
  const gate = document.getElementById("connectorGate");
  const messages = document.getElementById("chatMessages");
  const composer = document.querySelector(".chat-input-container");
  if (!gate || !messages || !composer) return;
  gate.hidden = paired;
  messages.style.display = paired ? "" : "none";
  composer.style.display = paired ? "" : "none";
  // The session controls live in the header, outside the hidden chat body, so
  // they stay clickable unless hidden explicitly — "New chat" while gated
  // creates nothing and just looks broken. The active-project chip beside them
  // stays: it tells the user which project is being paired.
  const sessionToggle = document.getElementById("sessionSelectorToggle");
  const sessionPanel = document.getElementById("sessionSelectorPanel");
  if (sessionToggle) sessionToggle.style.display = paired ? "" : "none";
  if (sessionPanel) {
    if (!paired) {
      sessionPanel.hidden = true;
      sessionToggle?.setAttribute("aria-expanded", "false");
    }
    sessionPanel.style.display = paired ? "" : "none";
  }
}

function setConnectorGateStatus(text) {
  const status = document.getElementById("connectorGateStatus");
  if (status) status.textContent = text;
}

let connectorGateRetryTimer = null;

function clearConnectorGateRetry() {
  if (connectorGateRetryTimer) {
    window.clearTimeout(connectorGateRetryTimer);
    connectorGateRetryTimer = null;
  }
}

/**
 * Single place that turns an installation list (or a failed fetch — pass
 * `failure`) into gate state. Every fetch of the list flows through here, so
 * the gate and the account panel's connector list can never disagree about
 * whether the account is paired.
 */
function updateConnectorGateFromInstallations(installations, failure = null) {
  clearConnectorGateRetry();
  const gated = shouldGateChat({
    authConfig: getAuthConfig(),
    currentUser: getCurrentUser(),
    legacyApiKey: getLegacyApiKey(),
    installations,
  });
  applyConnectorGate(!gated);
  if (!gated) return;
  setConnectorGateStatus(
    failure
      ? failure.message || "Could not check whether Microsoft Project is paired."
      : "Waiting for a pairing request from Microsoft Project…"
  );
  if (failure) {
    // An already-paired account has no other unlock path: discovery completion
    // fires once per pairing and theirs is long past, so without a retry this
    // error screen is terminal until the pane reloads. Single timer — each
    // retry either clears it or schedules the next.
    connectorGateRetryTimer = window.setTimeout(() => {
      connectorGateRetryTimer = null;
      refreshConnectorGate();
    }, 8000);
  }
}

// Monotonic stamp for installation-list fetches. Gate state must follow the
// NEWEST request, not the last response to arrive: without this, a slow stale
// fetch (started before a pairing completed) can resolve after a fresh one and
// re-lock the gate with an empty list — and since that response is a success,
// the failure retry never arms.
let connectorInstallationsFetchSeq = 0;

async function fetchConnectorInstallations() {
  const seq = ++connectorInstallationsFetchSeq;
  try {
    const installations = await listConnectorInstallations();
    return { installations, failure: null, stale: seq !== connectorInstallationsFetchSeq };
  } catch (failure) {
    return { installations: null, failure, stale: seq !== connectorInstallationsFetchSeq };
  }
}

async function refreshConnectorGate() {
  if (!connectorGateRequired()) {
    clearConnectorGateRetry();
    applyConnectorGate(true);
    return;
  }
  const expectedAccountGeneration = accountStateGeneration;
  const { installations, failure, stale } = await fetchConnectorInstallations();
  if (stale || expectedAccountGeneration !== accountStateGeneration) return;
  updateConnectorGateFromInstallations(installations, failure);
}

const CONNECTOR_PAIRING_POLL_MS = 5000;
const CONNECTOR_PAIRING_POLL_LIMIT = 24; // ~2 minutes

let connectorPairingPollGeneration = 0;

/**
 * After an approval the connector consumes the pairing on its own poll cycle,
 * which can take anywhere from a couple of seconds to over a minute. A
 * one-shot delayed refresh cannot cover that: if it fires before the
 * connector finishes, the empty list is a SUCCESSFUL response — so the
 * failure-only retry never arms and the gate stays shut until the pane
 * reloads. Poll until the installation appears instead, bounded so an
 * abandoned pairing does not poll forever. Each tick renders the panel and
 * the gate from one fetch; a newer poll or an account switch cancels the
 * loop.
 */
function pollConnectorInstallationsUntilPaired() {
  const generation = ++connectorPairingPollGeneration;
  const expectedAccountGeneration = accountStateGeneration;
  let attempts = 0;
  const tick = async () => {
    if (generation !== connectorPairingPollGeneration) return;
    if (expectedAccountGeneration !== accountStateGeneration) return;
    attempts += 1;
    const installations = await refreshConnectorInstallations();
    if (generation !== connectorPairingPollGeneration) return;
    if (expectedAccountGeneration !== accountStateGeneration) return;
    if (Array.isArray(installations) && installations.length > 0) return;
    if (attempts >= CONNECTOR_PAIRING_POLL_LIMIT) return;
    window.setTimeout(tick, CONNECTOR_PAIRING_POLL_MS);
  };
  window.setTimeout(tick, CONNECTOR_PAIRING_POLL_MS);
}

async function handleConnectorDiscoveryCompleted(candidate) {
  const prompt = document.getElementById("connectorDiscoveryPrompt");
  if (!prompt) return;
  const expectedAccountGeneration = accountStateGeneration;
  const connectorHint = String(candidate?.connector_hint || "");
  const fetched = await fetchConnectorInstallations();
  if (fetched.stale || expectedAccountGeneration !== accountStateGeneration) return;
  const installations = fetched.installations; // null when the fetch failed
  // The gate rides the installation list alone; the hint only decides whether
  // to show the "connected" toast. This completion signal fires ONCE per
  // pairing (the controller dedupes by pairing_id before invoking us), so a
  // transient fetch error or a connector_hint mismatch here must not strand
  // the gate shut after a pairing that actually succeeded.
  if (installations) {
    await refreshConnectorInstallations(installations);
  } else {
    refreshConnectorGate();
  }
  const installationFound =
    connectorHint.length > 0 &&
    (installations || []).some((installation) =>
      String(installation.installation_id || "").endsWith(connectorHint)
    );
  if (!installationFound) {
    hideConnectorDiscoveryPrompt();
    return;
  }
  currentDiscoveryCandidate = null;
  document.getElementById("connectorDiscoveryTitle").textContent =
    "Microsoft Project paired";
  document.getElementById("connectorDiscoveryDescription").textContent =
    "Microsoft Project is ready to sync.";
  document.getElementById("connectorDiscoveryCode").textContent = "";
  document.getElementById("connectDiscoveredConnectorBtn").disabled = true;
  document.getElementById("dismissDiscoveredConnectorBtn").disabled = true;
  prompt.hidden = false;
  // Panel and gate were already rendered above from the fetched list — no
  // second request here.
  window.setTimeout(() => {
    if (!currentDiscoveryCandidate) prompt.hidden = true;
  }, 3000);
}

function updateConnectorDiscoveryProject(stableIdentity) {
  const runtimeConfig = getAuthConfig();
  if (!getCurrentUser() || !runtimeConfig?.connector_discovery?.enabled) {
    connectorDiscoveryController.stop();
    return;
  }
  connectorDiscoveryController.updateProject({
    stableIdentity,
    environmentId: runtimeConfig.environment_id,
    config: runtimeConfig.connector_discovery,
    frontendVersion: "taskpane-1",
  }).catch((error) => {
    console.warn("Connector discovery project binding failed:", error);
  });
}

function resetUserSpecificClientState() {
  accountStateGeneration += 1;
  comparisonUploadAbortController?.abort();
  comparisonUploadAbortController = null;
  // Uploads and status polls belong to the account that started them.
  attachmentUploader.cancelAll();
  isUploadingComparisonFile = false;
  isInitialized = false;
  hideTypingIndicator();
  projectWatcher.resetForAccountChange();
  chatService.resetForAccountChange();
  // GID-83: the next account's events must not name this account's chat.
  latestReply = { sessionId: null, traceId: null };
  setSentryChatContext();
  chatHistory = [];
  sessionSelectorInstance?.resetForAccountChange();
  attachedFiles.length = 0;
  renderAttachedFileChips();
  hideComparisonBadge();
  connectorDiscoveryController.stop({ remove: true });
  hideConnectorDiscoveryPrompt();
  const pairingCode = document.getElementById("pairingCodeInput");
  if (pairingCode) pairingCode.value = "";
  const pairingRecovery = document.getElementById("pairingRecovery");
  if (pairingRecovery) pairingRecovery.open = false;
  const approvePairingButton = document.getElementById("approvePairingBtn");
  const denyPairingButton = document.getElementById("denyPairingBtn");
  if (approvePairingButton) {
    approvePairingButton.disabled = true;
    approvePairingButton.dataset.armed = "";
    approvePairingButton.textContent = "Approve";
  }
  if (denyPairingButton) denyPairingButton.disabled = true;
  const attachButton = document.getElementById("attach-button");
  if (attachButton) attachButton.disabled = false;
  const attachInput = document.getElementById("attach-file-input");
  const comparisonInput = document.getElementById("comparisonFileInput");
  if (attachInput) attachInput.value = "";
  if (comparisonInput) comparisonInput.value = "";
  const connectorList = document.getElementById("connectorInstallationsList");
  if (connectorList) connectorList.replaceChildren();
  const accountStatus = document.getElementById("accountStatus");
  if (accountStatus) accountStatus.textContent = "";
  const usageStatus = document.getElementById("usageStatus");
  if (usageStatus) usageStatus.textContent = "";
  const usagePeriod = document.getElementById("usagePeriod");
  if (usagePeriod) usagePeriod.textContent = "";
  const usageTotal = document.getElementById("usageTotal");
  if (usageTotal) usageTotal.textContent = "";
  const usageByModel = document.getElementById("usageByModel");
  if (usageByModel) usageByModel.replaceChildren();
  document.getElementById("projectInfo")?.remove();
  const messages = document.getElementById("chatMessages");
  if (messages) messages.innerHTML = "";
  const greeting = document.getElementById("user-greeting");
  if (greeting) {
    greeting.textContent = "";
    greeting.style.display = "none";
  }
}

/**
 * Renders the paired-connector panel AND drives the connector gate from the
 * same installation list, so the two views can never disagree about whether
 * the account is paired. Accepts an optional pre-fetched list (to avoid a
 * duplicate request when the caller just fetched one) and returns the list it
 * used — null when it could not be fetched.
 */
async function refreshConnectorInstallations(prefetched = null) {
  const expectedAccountGeneration = accountStateGeneration;
  const container = document.getElementById("connectorInstallations");
  const list = document.getElementById("connectorInstallationsList");
  const status = document.getElementById("accountStatus");
  if (!getAuthConfig()?.entra?.enabled || !getCurrentUser()) {
    container.hidden = true;
    list.replaceChildren();
    return null;
  }
  container.hidden = false;
  if (!Array.isArray(prefetched)) list.textContent = "Loading...";
  try {
    const fetched = Array.isArray(prefetched)
      ? { installations: prefetched, failure: null, stale: false }
      : await fetchConnectorInstallations();
    if (fetched.stale || expectedAccountGeneration !== accountStateGeneration) return null;
    if (fetched.failure) throw fetched.failure;
    const installations = fetched.installations;
    updateConnectorGateFromInstallations(installations);
    list.replaceChildren();
    if (installations.length === 0) {
      list.textContent = "No paired connectors.";
      return installations;
    }
    installations.forEach((installation) => {
      const row = document.createElement("div");
      row.className = "connector-installation-row";
      const label = document.createElement("span");
      label.className = "connector-installation-label";
      const pairedAt = installation.paired_at || installation.created_at;
      const pairedDate = pairedAt ? new Date(pairedAt) : null;
      label.textContent =
        pairedDate && !Number.isNaN(pairedDate.getTime())
          ? `Paired ${pairedDate.toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}`
          : "Paired connector";
      if (installation.installation_id) {
        label.title = `Connector installation ${installation.installation_id}`;
      }
      // Read-only on purpose. Revoking from here re-gated the chat immediately,
      // which reads as the add-in breaking itself; re-pairing from the ribbon
      // supersedes the old installation anyway.
      row.append(label);
      list.append(row);
    });
    return installations;
  } catch (error) {
    if (expectedAccountGeneration !== accountStateGeneration) return null;
    updateConnectorGateFromInstallations(null, error);
    list.textContent = "";
    status.textContent = error.message || "Could not load paired connectors";
    return null;
  }
}

async function refreshUsageSection() {
  const expectedAccountGeneration = accountStateGeneration;
  const status = document.getElementById("usageStatus");
  const periodEl = document.getElementById("usagePeriod");
  const totalEl = document.getElementById("usageTotal");
  const listEl = document.getElementById("usageByModel");
  status.textContent = "Loading...";
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/usage/me?recent=0`, {
      headers: getRequestAuthHeaders("GET"),
      credentials: getLegacyApiKey() ? "omit" : "include",
    });
    if (!response.ok) throw new Error(`Usage request failed (${response.status})`);
    const data = await response.json();
    if (expectedAccountGeneration !== accountStateGeneration) return;
    const { totalTokens, rows } = formatUsageRows(data.this_period_by_model);
    periodEl.textContent = formatSubscriptionPeriod(data.this_period_by_model) || "";
    totalEl.textContent = `${totalTokens.toLocaleString()} tokens total`;
    listEl.innerHTML = "";
    rows.forEach((row) => {
      const li = document.createElement("li");
      li.textContent = `${row.model}: ${row.tokens.toLocaleString()} tokens`;
      listEl.append(li);
    });
    status.textContent = rows.length === 0 ? "No usage yet this period." : "";
  } catch (error) {
    if (expectedAccountGeneration !== accountStateGeneration) return;
    status.textContent = "Couldn't load usage right now.";
    console.warn("Failed to load usage summary:", error);
  }
}

function bindAccountControls() {
  if (accountControlsBound) return;
  const panel = document.getElementById("accountPanel");
  const status = document.getElementById("accountStatus");
  const pairingCode = document.getElementById("pairingCodeInput");
  const approvePairingButton = document.getElementById("approvePairingBtn");
  const denyPairingButton = document.getElementById("denyPairingBtn");
  const linkButton = document.getElementById("linkLegacyAccountBtn");
  document.getElementById("accountButton").addEventListener("click", () => {
    panel.hidden = !panel.hidden;
    if (!panel.hidden) {
      refreshConnectorInstallations();
      // Without login every caller is the same "anonymous" user, so usage
      // would be everyone's combined: not shown.
      if (getAuthConfig()?.mode !== "disabled") refreshUsageSection();
    }
  });
  // GID-83: report anything in the flow, from the account menu.
  document.getElementById("reportProblemBtn")?.addEventListener("click", openProblemReport);
  document.getElementById("logoutButton").addEventListener("click", async () => {
    if (getLegacyApiKey()) {
      clearLegacyApiKey();
      resetUserSpecificClientState();
      panel.hidden = true;
      showAuthSetup();
      return;
    }
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: getRequestAuthHeaders("POST"),
      });
      if (!response.ok) throw new Error(`Sign out failed (${response.status})`);
      clearWebSession();
      clearLegacyApiKey();
      resetUserSpecificClientState();
      panel.hidden = true;
      showAuthSetup();
    } catch (error) {
      status.textContent = error.message || "Could not sign out";
    }
  });
  linkButton.addEventListener("click", async () => {
    status.textContent = "Opening Microsoft sign-in...";
    try {
      // Linking is an Entra sign-in like any other, so it goes through the same
      // terms check: a legacy-key user was never asked, and startApp() alone
      // would carry them into the app with a web session that has not accepted.
      const session = await signInWithOfficeDialog("link");
      status.textContent = "Account linked.";
      panel.hidden = true;
      await continueAfterSignIn(session);
    } catch (error) {
      status.textContent = error.message || "Linking failed";
    }
  });
  const typedPairingCode = () => pairingCode.value.trim().toUpperCase();
  const disarmApprove = () => {
    approvePairingButton.dataset.armed = "";
    approvePairingButton.textContent = "Approve";
  };
  pairingCode.addEventListener("input", () => {
    // Drop any armed "Confirm replace" state so it cannot carry over to a new code.
    disarmApprove();
    const hasCode = typedPairingCode().length > 0;
    approvePairingButton.disabled = !hasCode;
    denyPairingButton.disabled = !hasCode;
  });
  document.getElementById("refreshConnectorsBtn").addEventListener(
    "click",
    refreshConnectorInstallations
  );
  approvePairingButton.addEventListener("click", async () => {
    const expectedAccountGeneration = accountStateGeneration;
    const code = typedPairingCode();
    if (!code) return;
    // Approving used to require an Inspect round-trip first, purely to learn
    // whether the connector belonged to someone else. The approve endpoint
    // already answers that: it refuses with CONNECTOR_ACCOUNT_MISMATCH unless
    // the takeover is confirmed. Ask only when it actually says so, so the
    // common case is one press.
    const confirmReassignment = approvePairingButton.dataset.armed === "true";
    disarmApprove();
    try {
      await approveConnectorPairing(code, confirmReassignment);
      if (expectedAccountGeneration !== accountStateGeneration) return;
      status.textContent = "Pairing approved. The connector will finish automatically.";
      pairingCode.value = "";
      approvePairingButton.disabled = true;
      denyPairingButton.disabled = true;
      setConnectorGateStatus("Pairing approved. Finishing on the Microsoft Project side…");
      pollConnectorInstallationsUntilPaired();
    } catch (error) {
      if (expectedAccountGeneration !== accountStateGeneration) return;
      if (error.code === "CONNECTOR_ACCOUNT_MISMATCH") {
        // window.confirm() never displays in an Office task pane webview — it
        // returns false and the click is silently swallowed. Warn in-page and
        // require a second deliberate press instead.
        approvePairingButton.dataset.armed = "true";
        approvePairingButton.textContent = "Confirm replace";
        status.textContent =
          "This connector is linked to another account. Press Confirm replace to take it over.";
        return;
      }
      status.textContent = error.message || "Pairing failed";
    }
  });
  denyPairingButton.addEventListener("click", async () => {
    const expectedAccountGeneration = accountStateGeneration;
    const code = typedPairingCode();
    if (!code) return;
    try {
      await denyConnectorPairing(code);
      if (expectedAccountGeneration !== accountStateGeneration) return;
      status.textContent = "Connector pairing denied.";
      pairingCode.value = "";
      disarmApprove();
      approvePairingButton.disabled = true;
      denyPairingButton.disabled = true;
    } catch (error) {
      if (expectedAccountGeneration !== accountStateGeneration) return;
      status.textContent = error.message || "Could not deny pairing";
    }
  });
  document.getElementById("connectDiscoveredConnectorBtn").addEventListener(
    "click",
    async () => {
      const expectedAccountGeneration = accountStateGeneration;
      const candidate = currentDiscoveryCandidate;
      const presenceId = connectorDiscoveryController.presenceId;
      if (!candidate || !presenceId) return;
      // window.confirm() is suppressed in an Office task pane webview: it returns
      // false without ever showing a dialog, which silently swallowed this click
      // and made Connect look dead. The prompt itself already warns "Connecting
      // will replace this connector's previous account", so pressing Connect on
      // that warning IS the explicit confirmation.
      const confirmReassignment =
        candidate.ownership_state === "other_user" ||
        forcedReassignmentPairingId === candidate.pairing_id;
      const connectButton = document.getElementById("connectDiscoveredConnectorBtn");
      connectButton.disabled = true;
      try {
        await approveDiscoveredConnectorPairing(
          candidate.pairing_id,
          presenceId,
          confirmReassignment
        );
        if (expectedAccountGeneration !== accountStateGeneration) return;
        forcedReassignmentPairingId = "";
        // The gate opens when the installation appears — via the discovery
        // completion signal or the poll below, whichever lands first. Say so,
        // otherwise approving looks like it did nothing.
        setConnectorGateStatus("Approved. Finishing on the Microsoft Project side…");
        connectorDiscoveryController.markApproved(candidate);
        renderConnectorDiscoveryCandidates([{ ...candidate, status: "approved" }]);
        connectorDiscoveryController.refreshNow();
        pollConnectorInstallationsUntilPaired();
      } catch (error) {
        if (expectedAccountGeneration !== accountStateGeneration) return;
        if (error.code === "CONNECTOR_ACCOUNT_MISMATCH" && !confirmReassignment) {
          // Arm an explicit second press rather than replacing another account's
          // connector off the back of a single click (no window.confirm here: it
          // does not display inside the Office task pane).
          forcedReassignmentPairingId = candidate.pairing_id;
          document.getElementById("connectorDiscoveryDescription").textContent =
            "This connector is linked to another account. Press Pair again to replace that connection.";
        } else {
          document.getElementById("connectorDiscoveryDescription").textContent =
            error.message || "Could not pair Microsoft Project.";
        }
        connectButton.disabled = false;
      }
    }
  );
  document.getElementById("dismissDiscoveredConnectorBtn").addEventListener(
    "click",
    async () => {
      const expectedAccountGeneration = accountStateGeneration;
      const candidate = currentDiscoveryCandidate;
      const presenceId = connectorDiscoveryController.presenceId;
      if (!candidate || !presenceId) return;
      try {
        await dismissDiscoveredConnectorPairing(candidate.pairing_id, presenceId);
        if (expectedAccountGeneration !== accountStateGeneration) return;
        hideConnectorDiscoveryPrompt();
        connectorDiscoveryController.refreshNow();
      } catch (error) {
        if (expectedAccountGeneration !== accountStateGeneration) return;
        document.getElementById("connectorDiscoveryDescription").textContent =
          error.message || "Could not dismiss connector pairing.";
      }
    }
  );
  accountControlsBound = true;
}

async function startApp() {
  if (isStartingApp) return;
  const expectedAccountGeneration = accountStateGeneration;
  isStartingApp = true;
  try {
    document.getElementById("auth-setup").style.display = "none";
    document.getElementById("app-body").style.display = "flex";
    // Applied synchronously so the chat never flashes before the async pairing
    // check below resolves; refreshConnectorGate() opens it if already paired.
    // Exempt sessions get the opposite treatment for the same reason: a legacy
    // user signing in after a gated Entra session must not see the stale gate.
    applyConnectorGate(!connectorGateRequired());
    bindAccountControls();
    document.getElementById("linkLegacyAccountBtn").hidden = !(
      getAuthConfig()?.legacy_linking_enabled && getLegacyApiKey()
    );
    document.getElementById("pairingRecovery").hidden = !(
      getAuthConfig()?.entra?.enabled && getCurrentUser()
    );
    // The gear stays visible without login too: it holds "Report a problem"
    // (GID-83). Only signing out needs a signed-in user.
    document.getElementById("logoutButton").hidden = getAuthConfig()?.mode === "disabled";
    document.getElementById("usageSection").hidden = getAuthConfig()?.mode === "disabled";
    document.getElementById("logoutButton").textContent = getLegacyApiKey()
      ? "Disconnect"
      : "Sign out";
    forceHostRepaint();

    // Fetch and display user name
    try {
      const resp = await fetch(`${API_CONFIG.baseURL}/auth/me`, {
        credentials: getLegacyApiKey() ? "omit" : "include",
        headers: getRequestAuthHeaders("GET"),
      });
      if (expectedAccountGeneration !== accountStateGeneration) return;
      if (resp.ok) {
        const user = await resp.json();
        const name = user.name || user.email || "";
        if (name) {
          const el = document.getElementById("user-greeting");
          el.textContent = `Hello, ${name}`;
          el.style.display = "block";
        }
      }
    } catch (e) {
      console.warn("Failed to fetch user info:", e);
    }

    if (expectedAccountGeneration !== accountStateGeneration) return;

    viewManagementService.getActiveView();
    viewManagementService.testGetSelectedView();
    refreshConnectorGate();
    await initializeChat(expectedAccountGeneration);
    if (expectedAccountGeneration !== accountStateGeneration) return;
    sessionSelectorInstance?.refresh();
  } finally {
    isStartingApp = false;
    // The account changed while this start was running. Restart for the new
    // one -- unless it is waiting on the terms screen, which starts the app
    // itself once they are accepted. Restarting here would open the app behind
    // that screen and skip it.
    if (
      expectedAccountGeneration !== accountStateGeneration &&
      (getCurrentUser() || getLegacyApiKey()) &&
      !termsGate?.isShowing()
    ) {
      startApp();
    }
  }
}

window.addEventListener("auth-session-invalid", (event) => {
  clearWebSession();
  clearLegacyApiKey();
  resetUserSpecificClientState();
  showAuthSetup();
  if (event?.detail?.credentialMethod !== "legacy") {
    // /auth/session clears an expired HttpOnly cookie server-side. This keeps a
    // later legacy-key request from carrying two credential types in dual mode.
    recoverWebSession().catch(() => {});
  }
});

Office.onReady(async (info) => {
  console.log("Office.onReady called with info:", info);
  console.log("Host type:", info.host);
  console.log("Platform:", info.platform);

  if (info.host === Office.HostType.Project) {
    console.log("MS Project detected - initializing chat");
    document.getElementById("sideload-msg").style.display = "none";

    await initializeAuthentication();
  } else {
    console.log("Not running in MS Project. Host type:", info.host);
    console.log("Expected:", Office.HostType.Project);
  }
});

// GID-61: see the resize handler below for why this exists. Toggling a
// transform on/off forces the browser to create then dispose a GPU
// compositing layer for the element, which forces a genuine recomposite
// instead of reusing a stale cached frame.
function forceHostRepaint() {
  const el = document.body;
  if (!el) return;
  const prevTransform = el.style.transform;
  el.style.transform = "translateZ(0)";
  void el.offsetHeight; // eslint-disable-line no-void -- force sync layout before restoring
  el.style.transform = prevTransform;
}

async function initializeChat(expectedAccountGeneration = accountStateGeneration) {
  console.log("initializeChat() called");

  const messageInput = document.getElementById("messageInput");
  const chatMessages = document.getElementById("chatMessages");
  const comparisonUploadButton = document.getElementById("comparisonUploadButton");
  const comparisonFileInput = document.getElementById("comparisonFileInput");
  const comparisonReportButton = document.getElementById("comparisonReportButton");
  const comparisonClearButton = document.getElementById("comparisonClearButton");
  const statusIndicator = document.querySelector(".status-indicator");

  try {
    updateStatus("Connecting...", "connecting");
    console.log("Checking backend health...");

    try {
      const healthData = await healthAPI.checkHealth();
      if (expectedAccountGeneration !== accountStateGeneration) return;
      console.log("Raw health response:", healthData);
      console.log("Health status:", healthData.status);
      console.log("Health type:", typeof healthData.status);

      if (healthData.status === "healthy" || healthData.status === "degraded") {
        console.log("Backend health check passed:", healthData);
        const backendIdentityMode = String(healthData.identity_mode || "dual").toLowerCase();
        window.__BACKEND_IDENTITY_MODE__ = backendIdentityMode;
        window.backendRuntimeConfig = {
          ...(window.backendRuntimeConfig || {}),
          identity_mode: backendIdentityMode,
        };

        const apiTest = new APIAlignmentTest();
        await apiTest.testHealthCheck();
        if (expectedAccountGeneration !== accountStateGeneration) return;
        console.log("API Test Results:", apiTest.testResults);
      } else {
        console.error("Health check failed - status:", healthData.status);
        throw new Error(`Backend health check failed: ${healthData.status}`);
      }
    } catch (error) {
      console.error("Health check error:", error);
      throw error;
    }


    // Context loading is now fully deferred to first message (checkAndUpdateProjectContext)
    // No need to pre-fetch tasks here


    console.log("Starting chatService.initialize()...");
    try {
      await chatService.initialize();
      if (expectedAccountGeneration !== accountStateGeneration) return;
      console.log("chatService.initialize() completed successfully");
    } catch (error) {
      console.error("chatService.initialize() failed:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw error;
    }

    isInitialized = true;
    console.log("isInitialized set to true");
    updateStatus("Online", "online");
    console.log("Status updated to Online");

    chatService.onDocumentChange = handleDocumentChange;

    mountSessionSelector();
    wireSessionHistoryRenderer();
    startProjectWatcher();

    await displayProjectInfo();
  } catch (error) {
    if (expectedAccountGeneration !== accountStateGeneration) return;
    console.error("Failed to initialize chat service:", error);
    updateStatus("Offline", "offline");
    showErrorMessage(
      `Failed to connect to chat service: ${error.message}. Please ensure you are in MS Project with an open project file.`
    );
  }

  if (expectedAccountGeneration !== accountStateGeneration) return;

  if (!chatUiHandlersBound) {
    messageInput.addEventListener("input", function () {
      applyComposerLayout(this);

      updateSendButtonState();
    });

    // Re-run the textarea auto-grow when the task pane is resized. The "input"
    // handler only fires while typing, so without this the height stays stale
    // after a resize and wrapped text gets clipped.
    let repaintDebounceTimer = null;
    window.addEventListener("resize", function () {
      // Width change can wrap/unwrap the text, so re-evaluate the layout too.
      applyComposerLayout(messageInput);

      // GID-61: dragging the Office task-pane's sidebar width sometimes
      // leaves the WebView2 host showing a stale composited frame on top of
      // the freshly-laid-out one (the whole chat UI appears doubled/
      // overlapping). This is a host repaint issue, not a DOM bug -- every
      // mount/init path in this file is guarded against re-running (see
      // sessionSelectorMounted / chatUiHandlersBound above), so there is no
      // code path that duplicates the DOM itself. Nudging the compositor via
      // a throwaway transform forces WebView2 to recomposite from the
      // current DOM instead of blitting its cached frame. Debounced to
      // after the resize settles, not on every intermediate drag frame.
      if (repaintDebounceTimer) clearTimeout(repaintDebounceTimer);
      repaintDebounceTimer = setTimeout(forceHostRepaint, 150);
    });

    messageInput.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    });

    const sendButton = document.getElementById("sendButton");
    sendButton.addEventListener("click", function () {
      if (!sendButton.disabled) {
        sendMessage();
      }
    });

    initModelTierDropdown();
    initRouteListboxes();

    const stopButton = document.getElementById("stopButton");
    stopButton.addEventListener("click", function () {
      chatService.abort();
      hideTypingIndicator();
      addMessage("Stopped.", "system");
    });

    if (comparisonUploadButton && comparisonFileInput) {
      comparisonUploadButton.addEventListener("click", () => {
        if (!isUploadingComparisonFile) {
          comparisonFileInput.click();
        }
      });

      comparisonFileInput.addEventListener("change", async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await uploadComparisonFile(file);
        comparisonFileInput.value = "";
      });
    }

    if (comparisonClearButton) {
      comparisonClearButton.addEventListener("click", () => {
        clearComparisonAttachment();
      });
    }

    if (comparisonReportButton) {
      comparisonReportButton.addEventListener("click", () => {
        generateComparisonReport();
      });
    }

    bindAttachFileHandlers();

    chatMessages.addEventListener("click", () => {
      messageInput.focus();
    });

    chatUiHandlersBound = true;
  }

  messageInput.focus();



  updateSendButtonState();
}

// --- File attachment (T14, GID-220) ----------------------------------------
// attachedFiles is the source of truth for the chip strip: one entry per file
// in the current session, in whatever state it is in. Entries are replaced,
// never edited in place (updateAttachedEntry). Shape:
//   { local_id, file, file_id, message_id, filename, size, status, stage,
//     error, session_id, was_summarised?, removing? }
// `file` is the picked File, kept so a failed upload can be retried without
// asking the user to find it again. Uploading/polling/retrying lives in
// attachmentUploads.js; this section only wires it to the DOM.
// The chip × calls DELETE /chat/{session_id}/attach/{message_id or file_id},
// which removes the ATTACHED_FILE message (or cancels a file still being
// read) so its content stops entering the LLM context on later turns.
const attachedFiles = [];
let pendingAttachSession = null;

const attachmentUploader = createAttachmentUploader({
  api: chatAPI,
  onUpdate: (localId, patch) => {
    if (updateAttachedEntry(localId, patch)) renderAttachedFileChips();
  },
  onReady: (localId, result) => {
    // The chip may have been dismissed, or a session reload may already have
    // drawn this file's bubble from the saved messages.
    if (!findAttachedEntry(localId)) return;
    if (findAttachedFileBubble(result.message_id)) return;
    addAttachedFileMessage(result);
  },
});

function findAttachedEntry(localId) {
  return attachedFiles.find((f) => f.local_id === localId) || null;
}

function updateAttachedEntry(localId, patch) {
  const idx = attachedFiles.findIndex((f) => f.local_id === localId);
  if (idx < 0) return false;
  attachedFiles[idx] = { ...attachedFiles[idx], ...patch };
  return true;
}

function removeAttachedEntry(localId) {
  const idx = attachedFiles.findIndex((f) => f.local_id === localId);
  if (idx >= 0) attachedFiles.splice(idx, 1);
}

function findAttachedFileBubble(messageId) {
  if (!messageId) return null;
  const bubbles = document.querySelectorAll(".attached-file-message");
  return Array.from(bubbles).find((el) => el.dataset.messageId === String(messageId)) || null;
}

// Attaching from a blank "New chat" has no session yet — sessions are created
// lazily on first message (chatService.js:_ensureSessionForActiveProject).
// Several picks made before it exists share one creation, or each would make
// its own session and the files would scatter across them.
function ensureAttachSession() {
  if (chatService.currentSessionId) return Promise.resolve(chatService.currentSessionId);
  if (!pendingAttachSession) {
    pendingAttachSession = chatService
      ._ensureSessionForActiveProject()
      .then((session) => session?.session_id || chatService.currentSessionId || null)
      .finally(() => {
        pendingAttachSession = null;
      });
  }
  return pendingAttachSession;
}

function bindAttachFileHandlers() {
  const attachBtn = document.getElementById("attach-button");
  const fileInput = document.getElementById("attach-file-input");
  if (!attachBtn || !fileInput) return;

  attachBtn.addEventListener("click", () => fileInput.click());

  fileInput.addEventListener("change", (e) => {
    const files = Array.from((e.target && e.target.files) || []);
    fileInput.value = "";
    if (files.length) attachPickedFiles(files);
  });

  // One delegated listener: the strip re-renders on every status change.
  const chipsEl = document.getElementById("attached-files-chips");
  chipsEl?.addEventListener("click", (event) => {
    const button = event.target;
    const action = button?.dataset?.action;
    const localId = button?.dataset?.localId;
    if (!action || !localId || button.disabled) return;
    if (action === "remove") removeAttachedFile(localId);
    else if (action === "retry") retryAttachedFile(localId);
  });
}

async function attachPickedFiles(files) {
  const verdict = checkPickAgainstLimit(attachedFiles, files.length);
  if (!verdict.accepted) {
    addMessage(verdict.message, "system");
    return;
  }

  const expectedAccountGeneration = accountStateGeneration;
  // A chip for every file at once, before the session or the network can stall.
  const entries = files.map((file) => createPendingEntry(file, chatService.currentSessionId || null));
  attachedFiles.push(...entries);
  renderAttachedFileChips();

  let sessionId = null;
  try {
    sessionId = await ensureAttachSession();
  } catch (err) {
    if (expectedAccountGeneration !== accountStateGeneration) return;
    console.error("Could not start a chat session for attached files:", err);
    for (const entry of entries) {
      updateAttachedEntry(entry.local_id, {
        status: ATTACHMENT_STATUS.FAILED,
        error: err?.message || "Could not start a chat for this file",
      });
    }
    renderAttachedFileChips();
    return;
  }
  if (expectedAccountGeneration !== accountStateGeneration) return;

  // A session switch while the session was being created drops these chips.
  const live = entries.filter((entry) => findAttachedEntry(entry.local_id));
  if (!sessionId) {
    live.forEach((entry) => removeAttachedEntry(entry.local_id));
    renderAttachedFileChips();
    addMessage("Cannot attach file: no active project. Open a project in MS Project first.", "system");
    return;
  }
  live.forEach((entry) => startAttachedUpload(entry.local_id, sessionId));
}

function startAttachedUpload(localId, sessionId) {
  const entry = findAttachedEntry(localId);
  if (!entry?.file) return;
  // Remember the session this file was attached under, so it can still be
  // deleted/"released" after a Save-As rebind or session switch that moves
  // chatService.currentSessionId off this session (GID-93).
  updateAttachedEntry(localId, { session_id: sessionId });
  attachmentUploader.enqueue({ localId, file: entry.file, sessionId });
}

async function retryAttachedFile(localId) {
  const entry = findAttachedEntry(localId);
  if (!entry || entry.status !== ATTACHMENT_STATUS.FAILED || !entry.file) return;
  const verdict = checkPickAgainstLimit(attachedFiles, 1);
  if (!verdict.accepted) {
    addMessage(verdict.message, "system");
    return;
  }
  const expectedAccountGeneration = accountStateGeneration;
  let sessionId = entry.session_id;
  if (!sessionId) {
    try {
      sessionId = await ensureAttachSession();
    } catch (err) {
      console.error("Could not start a chat session for a retried file:", err);
      sessionId = null;
    }
    if (expectedAccountGeneration !== accountStateGeneration) return;
  }
  if (!sessionId) {
    addMessage("Cannot attach file: no active project. Open a project in MS Project first.", "system");
    return;
  }
  startAttachedUpload(localId, sessionId);
}

async function removeAttachedFile(localId) {
  const expectedAccountGeneration = accountStateGeneration;
  const entry = findAttachedEntry(localId);
  if (!entry || entry.removing) return;
  // Delete against the session the file was ATTACHED under (captured at attach
  // time), not the currently-active session. After a Save-As rebind / session
  // switch, chatService.currentSessionId points elsewhere, so a session-scoped
  // delete against it 404s and the chip gets permanently "stuck" (GID-93).
  const target = { ...entry, session_id: entry.session_id || chatService.currentSessionId };

  updateAttachedEntry(localId, { removing: true });
  renderAttachedFileChips();
  try {
    await attachmentUploader.remove(target);
  } catch (err) {
    if (expectedAccountGeneration !== accountStateGeneration) return;
    updateAttachedEntry(localId, { removing: false });
    renderAttachedFileChips();
    addMessage(`Could not remove attached file: ${err?.message || "Remove failed"}`, "system");
    return;
  }
  if (expectedAccountGeneration !== accountStateGeneration) return;
  // Re-read: a processing file can turn ready (and get a bubble) mid-delete.
  const latest = findAttachedEntry(localId) || entry;
  removeAttachedEntry(localId);
  renderAttachedFileChips();
  findAttachedFileBubble(latest.message_id)?.remove();
}

// Stops client-side work for files that no longer belong on screen. The server
// keeps reading them; they show up from the saved messages on return.
function cancelAttachmentWork(entries) {
  for (const entry of entries) attachmentUploader.cancel(entry.local_id);
}

function renderAttachedFileChips() {
  const chipsEl = document.getElementById("attached-files-chips");
  if (!chipsEl) return;

  if (!attachedFiles.length) {
    chipsEl.hidden = true;
    chipsEl.innerHTML = "";
    return;
  }

  chipsEl.hidden = false;
  chipsEl.innerHTML = buildAttachedChipsHtml(attachedFiles);
}

// Render an ATTACHED_FILE bubble in the transcript. This mirrors the backend's
// ATTACHED_FILE message_type so when sessions are re-hydrated later, the same
// renderer can be used by routing through renderAttachedFileMessage(msg).
function addAttachedFileMessage(meta) {
  const chatMessages = document.getElementById("chatMessages");
  if (!chatMessages) return;

  const filename = meta.filename || "attached file";
  const size = formatFileSize(Number(meta.size) || 0);
  const summarised = meta.was_summarised ? " · summarised" : "";

  const messageDiv = document.createElement("div");
  messageDiv.className = "message user-message attached-file-message";
  messageDiv.dataset.messageId = String(meta.message_id || "");
  const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  messageDiv.innerHTML = `
    <div class="message-avatar">
      <img src="../../assets/logo-filled.png" alt="You" />
    </div>
    <div class="message-content">
      <div class="message-text">
        <span class="attached-msg">📎 <span class="attached-filename" dir="auto" title="${escapeHtml(filename)}">${escapeHtml(filename)}</span>` +
        ` <span class="attached-meta">${escapeHtml(size)}${escapeHtml(summarised)}</span></span>
      </div>
      <div class="message-time">${time}</div>
    </div>
  `;

  chatMessages.appendChild(messageDiv);
  chatMessages.scrollTop = chatMessages.scrollHeight;
}
// --- end file attachment ---------------------------------------------------

// --- Rich-content rehydration (T?) -----------------------------------------
// Some assistant messages carry rich content beyond their text: a Download
// XML button, a ribbon-sync instruction, an attached-file chip. That extra
// content is derived from the message's `metadata` (persisted server-side —
// see backend orchestrator.py) but is NOT part of `content`, so a naive
// re-render of stored messages (session rehydration on project switch) used
// to drop it silently. These helpers are shared by the live post-response
// path AND the rehydration path so the two can't drift apart again.

// extractDownloadInfo / classifyMessageForRender live in messageRenderDecisions.js
// (pure, unit-tested independently of the DOM).

// Build the download button (+ optional ribbon-sync note) into an existing
// message-content element. Idempotent: no-ops if one is already present.
function renderDownloadSection(messageContentEl, info) {
  if (!messageContentEl || !info || !info.downloadUrl) return;
  if (messageContentEl.querySelector(".download-section")) return;

  const { downloadUrl, fileId, fileName, label, bridgeSyncCommandId } = info;

  const downloadButton = document.createElement("div");
  downloadButton.className = "download-section";
  downloadButton.innerHTML = `
    <div class="download-button-container">
      <button type="button" class="download-button" ${fileId ? `data-file-id="${fileId}"` : ""}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7,10 12,15 17,10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        ${escapeHtml(label || "Download XML")}
      </button>
    </div>
    ${bridgeSyncCommandId ? `
      <div class="ribbon-sync-instruction">
        To update Microsoft Project, click <strong>Novatime AI &gt; Sync Gantt</strong> in the Project ribbon.
      </div>
    ` : ""}
  `;
  messageContentEl.appendChild(downloadButton);

  const downloadBtn = downloadButton.querySelector(".download-button");
  if (downloadBtn) {
    downloadBtn.addEventListener("click", async () => {
      const originalText = downloadBtn.innerHTML;
      try {
        downloadBtn.disabled = true;
        downloadBtn.innerHTML = "Downloading...";

        const { blob, filename } = await filesAPI.downloadFile(downloadUrl, fileName);
        const objectUrl = globalThis.URL.createObjectURL(blob);
        const tempAnchor = document.createElement("a");
        tempAnchor.href = objectUrl;
        tempAnchor.download = filename || fileName;
        tempAnchor.style.display = "none";
        document.body.appendChild(tempAnchor);
        tempAnchor.click();
        document.body.removeChild(tempAnchor);
        globalThis.setTimeout(() => globalThis.URL.revokeObjectURL(objectUrl), 1000);

        downloadBtn.disabled = false;
        downloadBtn.innerHTML = originalText;
      } catch (err) {
        console.error("Download click error:", err);
        addMessage(`Download failed: ${err.message || "authentication or file access error"}`, "bot");
        downloadBtn.disabled = false;
        downloadBtn.innerHTML = originalText;
      }
    });
  }
}

// Build the change-summary block (coarse count line + collapsible details)
// into an existing message-content element (GID-95). Idempotent: no-ops if
// one is already present, same pattern as renderDownloadSection above.
function renderToolCallSummary(messageContentEl, toolCallSummary) {
  if (!messageContentEl || !toolCallSummary || !toolCallSummary.groups || toolCallSummary.groups.length === 0) return;
  if (messageContentEl.querySelector(".tool-call-summary")) return;

  const coarseLine = buildCoarseSummaryLine(toolCallSummary.groups);
  const detailLines = buildDetailLines(toolCallSummary.groups);

  const section = document.createElement("div");
  section.className = "tool-call-summary";
  section.innerHTML = `
    <div class="tool-call-summary-coarse">${escapeHtml(coarseLine)}</div>
    <details class="tool-call-summary-details">
      <summary>Show details</summary>
      <ul>
        ${detailLines.map((line) => `<li><strong>${escapeHtml(line.label)}:</strong> ${escapeHtml(line.text)}</li>`).join("")}
      </ul>
    </details>
    ${toolCallSummary.hasFailures ? '<div class="tool-call-summary-warning">Some changes could not be applied.</div>' : ""}
  `;
  messageContentEl.appendChild(section);
}

// Add one CSV control per rendered table. The DOM is the export source so the
// downloaded file always matches the sanitized table visible to the user.
// This is idempotent because messages can later be rebuilt from session history.
function renderTableDownloadSection(messageContentEl) {
  if (!messageContentEl || messageContentEl.querySelector(".table-download-section")) return;

  const tables = Array.from(messageContentEl.querySelectorAll(".message-text table"));
  if (tables.length === 0) return;

  const section = document.createElement("div");
  section.className = "download-section table-download-section";
  section.innerHTML = `
    <div class="download-button-container table-download-button-container">
      ${tables
        .map(
          (_, index) => `
            <button
              type="button"
              class="download-button table-download-button"
              data-table-index="${index}"
              title="Download this table as CSV"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7,10 12,15 17,10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              ${tables.length === 1 ? "Download CSV" : `Download Table ${index + 1} CSV`}
            </button>`
        )
        .join("")}
    </div>
  `;

  messageContentEl.appendChild(section);

  section.querySelectorAll(".table-download-button").forEach((button) => {
    button.addEventListener("click", () => {
      const tableIndex = Number(button.dataset.tableIndex);
      const table = tables[tableIndex];
      const originalContent = button.innerHTML;

      try {
        button.disabled = true;
        button.textContent = "Preparing CSV...";
        const filename =
          tables.length === 1 ? "generated-table.csv" : `generated-table-${tableIndex + 1}.csv`;
        downloadTableAsCsv(table, filename);
      } catch (err) {
        console.error("Table CSV download error:", err);
        addMessage(
          `CSV download failed: ${err.message || "this table could not be exported"}`,
          "bot"
        );
      } finally {
        button.disabled = false;
        button.innerHTML = originalContent;
      }
    });
  });
}
// --- end rich-content rehydration -------------------------------------------

function mountSessionSelector() {
  if (sessionSelectorMounted) return;
  try {
    sessionSelectorInstance = new SessionSelector(document);
    sessionSelectorInstance.mount();
    sessionSelectorMounted = true;
  } catch (err) {
    console.error("SessionSelector mount failed:", err);
  }
}

function wireSessionHistoryRenderer() {
  if (sessionHistoryWired) return;
  sessionHistoryWired = true;

  chatService.onSessionChange(({ sessionId, source, messages }) => {
    // GID-83: pane events name the chat now in view. Its newest reply's trace
    // is set again as that reply renders (attachRatingControls); until then
    // the previous chat's trace would point at the wrong turn.
    setSentryChatContext({
      sessionId,
      traceId: sessionId && latestReply.sessionId === sessionId ? latestReply.traceId : null,
    });
    // The comparison badge tracks the current session's selection on EVERY
    // change (including create/delete) — it must never show another
    // project's uploaded file.
    refreshComparisonBadge();

    // Replay history on user-driven switches and project changes only.
    // Skip on "create" (we just sent a message — the UI already shows it)
    // and on "delete" (the deleter clears its own UI).
    if (source !== "switch" && source !== "project_change" && source !== "new_chat_pending") {
      return;
    }
    renderSessionMessages(Array.isArray(messages) ? messages : []);
  });
}

function renderSessionMessages(messages) {
  const chatMessages = document.getElementById("chatMessages");
  if (!chatMessages) return;

  chatMessages.innerHTML = "";
  chatHistory = [];

  // Rebuild the staged file chips from THIS session's own attached files (below), so
  // the chip strip (with its remove ×) always reflects the current session: it no longer
  // carries a previous session's file over, and it no longer vanishes on switch (GID-96).
  // Files still uploading/being read (or failed) are not in the saved messages yet.
  // Keep them only when this render is the same session again (a reload of the
  // current chat); for any other session, stop their uploads and polling (GID-220).
  const shownSessionId = chatService.currentSessionId;
  const unsaved = attachedFiles.filter((f) => f.status && f.status !== ATTACHMENT_STATUS.READY);
  const keptUnsaved = unsaved.filter((f) => shownSessionId && f.session_id === shownSessionId);
  cancelAttachmentWork(unsaved.filter((f) => !keptUnsaved.includes(f)));
  attachedFiles.length = 0;

  renderWelcomeMessage();

  for (const msg of messages) {
    const decision = classifyMessageForRender(msg);
    if (decision.kind === "hidden") {
      continue;
    }
    if (decision.kind === "attached_file") {
      const fileMeta = {
        local_id: `message-${decision.messageId}`,
        message_id: decision.messageId,
        filename: decision.filename,
        size: decision.size,
        was_summarised: decision.wasSummarised,
        status: ATTACHMENT_STATUS.READY,
        session_id: shownSessionId,
      };
      addAttachedFileMessage(fileMeta);
      // Re-stage so the removable chip above the composer shows for this session.
      attachedFiles.push(fileMeta);
      continue;
    }
    const sender = msg.role === "assistant" ? "bot" : msg.role === "system" ? "system" : "user";
    const contentEl = renderHistoricalMessage(msg.content || "", sender, msg.created_at || null);
    if (sender === "bot") {
      renderTableDownloadSection(contentEl);
    }
    if (decision.downloadInfo) {
      renderDownloadSection(contentEl, decision.downloadInfo);
    }
    if (decision.toolCallSummary) {
      renderToolCallSummary(contentEl, decision.toolCallSummary);
    }
    // Last, so the thumbs sit under the whole reply, as on a live answer.
    if (sender === "bot" && !msg.metadata?.error) {
      attachRatingControls(contentEl, {
        sessionId: chatService.currentSessionId,
        messageId: msg.message_id || msg.id,
        traceId: msg.metadata?.langfuse_trace_id,
      });
    }
  }

  const savedMessageIds = new Set(attachedFiles.map((f) => String(f.message_id)));
  attachedFiles.push(
    ...keptUnsaved.filter((f) => !f.message_id || !savedMessageIds.has(String(f.message_id)))
  );

  // Render the (rebuilt) chip strip for this session's files, with their remove ×.
  renderAttachedFileChips();

  chatMessages.scrollTop = chatMessages.scrollHeight;
}

function renderWelcomeMessage() {
  const chatMessages = document.getElementById("chatMessages");
  if (!chatMessages) return;

  const welcomeDiv = document.createElement("div");
  welcomeDiv.className = "message bot-message";
  welcomeDiv.innerHTML = `
    <div class="message-avatar">
      <img src="../../assets/logo-filled.png" alt="Bot" />
    </div>
    <div class="message-content">
      <div class="message-text">Hello! I'm your Novatime AI Assistant. How can I help you today?</div>
      <div class="message-time">Just now</div>
    </div>
  `;
  chatMessages.appendChild(welcomeDiv);
}

function renderHistoricalMessage(text, sender, createdAtIso) {
  const chatMessages = document.getElementById("chatMessages");
  if (!chatMessages) return;

  const messageDiv = document.createElement("div");
  messageDiv.className = `message ${sender}-message`;

  const time = createdAtIso
    ? new Date(createdAtIso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  const formattedText =
    sender === "system"
      ? escapeHtml(text).replace(/\n/g, "<br>")
      : renderMarkdownSafely(text);

  messageDiv.innerHTML = `
    <div class="message-avatar">
      <img src="../../assets/logo-filled.png" alt="${sender === "user" ? "You" : sender === "system" ? "System" : "Bot"}" />
    </div>
    <div class="message-content">
      <div class="message-text">${formattedText}</div>
      <div class="message-time">${time}</div>
    </div>
  `;

  const messageText = messageDiv.querySelector(".message-text");
  if (isMostlyHebrew(text)) {
    messageText.dir = "rtl";
  }

  const messageContentEl = messageDiv.querySelector(".message-content");
  if (sender !== "system") {
    const copyBtn = makeCopyButton(() => messageText.innerText);
    messageContentEl.appendChild(copyBtn);
  }

  chatMessages.appendChild(messageDiv);
  chatHistory.push({ text, sender, time });
  return messageContentEl;
}

function startProjectWatcher() {
  if (!projectWatcherWired) {
    projectWatcherWired = true;
    projectWatcher.subscribe(({ guid, name, identities, correction }) => {
      updateConnectorDiscoveryProject(
        Array.isArray(identities) && identities.length > 0 ? identities : guid
      );
      if (!guid) return;
      const applied = correction
        ? chatService.applyIdentityCorrection({ guid, name })
        : chatService.setActiveProject({ guid, name });
      applied.catch((err) => {
        console.error("project identity update failed:", err);
      });
    });
  }

  const currentProject = projectWatcher.current;
  if (currentProject.guid) {
    const identities = projectWatcher.discoveryIdentities;
    updateConnectorDiscoveryProject(identities.length > 0 ? identities : currentProject.guid);
  }

  projectWatcher.start();
}

window.addEventListener("beforeunload", () => {
  connectorDiscoveryController.stop({ remove: true, keepalive: true });
});

function updateSendButtonState() {
  const messageInput = document.getElementById("messageInput");
  const sendButton = document.getElementById("sendButton");
  const modelTierToggle = document.getElementById("modelTierToggle");
  const comparisonReportButton = document.getElementById("comparisonReportButton");

  const hasText = messageInput.value.trim().length > 0;
  const canSend = hasText && !isTyping && isInitialized && !isUploadingComparisonFile;

  sendButton.disabled = !canSend;
  if (modelTierToggle) modelTierToggle.disabled = isTyping;
  if (comparisonReportButton) {
    comparisonReportButton.disabled = !(
      chatService.getComparisonAttachment() &&
      !isTyping &&
      isInitialized &&
      !isUploadingComparisonFile
    );
  }
}

// GID-218: handle for the Route listbox, kept so the send path can refresh its
// label after takeRouteOverride() resets the value (a programmatic .value write
// fires no change event).
let routeOverrideListbox = null;

/**
 * Replace the two native route <select> popups with in-document listboxes.
 *
 * The host drew the native popups in the wrong place on a multi-monitor setup —
 * over the Gantt grid instead of under the control. See listboxSelect.js.
 */
function initRouteListboxes() {
  routeOverrideListbox = enhanceSelect(document.getElementById("routeOverrideSelect"));
  enhanceSelect(document.getElementById("editHarnessModeSelect"));
}

function initModelTierDropdown() {
  const toggle = document.getElementById("modelTierToggle");
  const menu = document.getElementById("modelTierMenu");
  const label = document.getElementById("modelTierLabel");
  if (!toggle || !menu || !label) return;

  // Tier -> raw model id label. Populated at runtime from /models/tiers so
  // adding a tier is a backend config change. Until the fetch completes we
  // keep a minimal fallback so the picker still labels something reasonable.
  let tierLabels = {};
  let availableTiers = [];

  function applyTier(tier) {
    label.textContent = tierLabels[tier] || tier || "model";
    const optionEls = menu.querySelectorAll(".model-tier-option");
    optionEls.forEach((el) => {
      const isActive = el.dataset.tier === tier;
      el.classList.toggle("is-active", isActive);
      el.setAttribute("aria-selected", isActive ? "true" : "false");
    });
  }

  function openMenu() {
    menu.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    // Model ids are long and the pane can be dragged narrow, so check the menu
    // still fits before the user sees it. Same helper the route pickers use.
    fitMenuToViewport(toggle, menu);
  }

  function closeMenu() {
    menu.hidden = true;
    toggle.setAttribute("aria-expanded", "false");
  }

  function renderOptions(currentTier) {
    menu.innerHTML = "";
    availableTiers.forEach((entry) => {
      const li = document.createElement("li");
      li.role = "option";
      li.dataset.tier = entry.tier;
      li.className = "model-tier-option";
      li.id = `modelTierOpt_${entry.tier}`;
      li.textContent = entry.model_id;
      li.title = entry.model_id; // tooltip = full id, in case of CSS truncation
      const isActive = entry.tier === currentTier;
      li.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) li.classList.add("is-active");
      li.addEventListener("click", function () {
        // Click targets are constructed from server-provided tiers, so input
        // is always valid here. Apply unconditionally rather than gating on
        // setModelTier's return value (false when storage is blocked even
        // though the in-memory tier was updated).
        setModelTier(entry.tier);
        applyTier(entry.tier);
        closeMenu();
      });
      menu.appendChild(li);
    });
  }

  async function loadTiersFromBackend() {
    try {
      const data = await modelsAPI.getTiers();
      if (!data || !Array.isArray(data.tiers) || !data.tiers.length) return;
      availableTiers = data.tiers;
      tierLabels = {};
      availableTiers.forEach((t) => {
        tierLabels[t.tier] = t.model_id;
      });
      let current = getModelTier();
      if (!tierLabels[current]) {
        // Stored tier no longer exists on backend; pick default or first.
        current = data.default_tier && tierLabels[data.default_tier]
          ? data.default_tier
          : availableTiers[0].tier;
        setModelTier(current);
      }
      renderOptions(current);
      applyTier(current);
    } catch (_err) {
      // Silent fallback: keep whatever the static defaults gave us. Don't
      // crash the pane just because the tier list couldn't load.
    }
  }

  // Initial render with whatever's in localStorage; backend fetch updates.
  applyTier(getModelTier());
  loadTiersFromBackend();

  // No stopPropagation: the route pickers next door close themselves from their
  // own document listeners, and swallowing this click would leave their menus
  // open behind this one. The listener below ignores clicks on the toggle, so
  // this cannot close the menu it just opened.
  toggle.addEventListener("click", function () {
    if (toggle.disabled) return;
    if (menu.hidden) openMenu(); else closeMenu();
  });

  document.addEventListener("click", function (e) {
    if (!menu.hidden && !menu.contains(e.target) && !toggle.contains(e.target)) {
      closeMenu();
    }
  });
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "unknown";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(name) {
  if (!name || typeof name !== "string") return "";
  const parts = name.toLowerCase().split(".");
  return parts.length > 1 ? parts.pop() : "";
}

function clearComparisonAttachment() {
  chatService.clearComparisonAttachment();
  hideComparisonBadge();
  updateSendButtonState();
}

function hideComparisonBadge() {
  const comparisonBadge = document.getElementById("comparisonFileBadge");
  const reportBtn = document.getElementById("comparisonReportButton");
  const clearBtn = document.getElementById("comparisonClearButton");
  if (comparisonBadge) {
    comparisonBadge.style.display = "none";
    comparisonBadge.textContent = "";
  }
  if (reportBtn) reportBtn.style.display = "none";
  if (clearBtn) clearBtn.style.display = "none";
}

// Re-derive the badge from the CURRENT session's selection. Called on every
// session/project change: the selection is per chat session, so switching
// documents hides another project's file and switching back restores it.
function refreshComparisonBadge() {
  const attachment = chatService.getComparisonAttachment();
  if (attachment) {
    renderComparisonAttachmentBadge(attachment);
  } else {
    hideComparisonBadge();
  }
}

function renderComparisonAttachmentBadge(attachment) {
  const comparisonBadge = document.getElementById("comparisonFileBadge");
  const reportBtn = document.getElementById("comparisonReportButton");
  const clearBtn = document.getElementById("comparisonClearButton");
  if (!comparisonBadge || !attachment) return;

  comparisonBadge.textContent = `${attachment.filename} (${attachment.format.toUpperCase()})`;
  comparisonBadge.title = `Selected for comparison: ${attachment.filename}`;
  comparisonBadge.style.display = "inline-block";
  if (reportBtn) reportBtn.style.display = "inline-block";
  if (clearBtn) clearBtn.style.display = "inline-block";
  updateSendButtonState();
}

async function uploadComparisonFile(file) {
  const ext = getFileExtension(file.name);
  if (!SUPPORTED_COMPARE_EXTENSIONS.has(ext)) {
    addMessage("Only .xml and .mpp files are supported for Gantt comparison.", "system");
    return;
  }
  if (file.size > MAX_COMPARE_UPLOAD_SIZE) {
    addMessage(`File is too large (${formatFileSize(file.size)}). Max allowed is ${formatFileSize(MAX_COMPARE_UPLOAD_SIZE)}.`, "system");
    return;
  }

  const expectedAccountGeneration = accountStateGeneration;
  const abortController = new AbortController();
  comparisonUploadAbortController?.abort();
  comparisonUploadAbortController = abortController;
  isUploadingComparisonFile = true;
  updateSendButtonState();
  addMessage(`Uploading '${file.name}' for schedule comparison...`, "system");

  try {
    const uploadResponse = await filesAPI.uploadFile(file, {
      userId: chatService.userId,
      projectName: "MS Project Compare Upload",
      signal: abortController.signal,
    });

    if (
      expectedAccountGeneration !== accountStateGeneration ||
      abortController.signal.aborted
    ) return;

    const attachment = {
      file_id: uploadResponse.file_id,
      filename: uploadResponse.original_filename || file.name,
      mime_type: uploadResponse.file_type || file.type || "application/octet-stream",
      format: ext,
    };
    chatService.setComparisonAttachment(attachment);
    renderComparisonAttachmentBadge(attachment);
    addMessage(
      `Ready to compare using '${attachment.filename}'. Ask a specific question or request a full report.`,
      "system"
    );
  } catch (error) {
    if (
      expectedAccountGeneration !== accountStateGeneration ||
      abortController.signal.aborted
    ) return;
    console.error("Comparison upload failed:", error);
    sentryCaptureException(error, {
      fingerprint: ["frontend_compare_upload_failure"],
      tags: {
        subsystem: "compare_upload",
        feature: "upload",
      },
      extra: payloadSnapshot(
        {
          filename: file?.name,
          size: file?.size,
          extension: ext,
        },
        "compare_upload"
      ),
      level: "warning",
    });
    addMessage(`Failed to upload file for comparison: ${error.message || "Unknown error"}`, "bot");
  } finally {
    if (comparisonUploadAbortController === abortController) {
      comparisonUploadAbortController = null;
      isUploadingComparisonFile = false;
      updateSendButtonState();
    }
  }
}

async function generateComparisonReport() {
  if (
    !chatService.getComparisonAttachment() ||
    isTyping ||
    !isInitialized ||
    isUploadingComparisonFile
  ) {
    return;
  }
  const reportButton = document.getElementById("comparisonReportButton");
  if (reportButton) {
    reportButton.textContent = "Generating...";
    reportButton.setAttribute("aria-busy", "true");
  }

  try {
    await sendMessage({
      message: "Generate a complete comparison report.",
      comparisonAction: "full_report",
      displayUserMessage: false,
    });
  } finally {
    if (reportButton) {
      reportButton.textContent = "Generate report";
      reportButton.removeAttribute("aria-busy");
    }
  }
}

async function sendMessage(options = {}) {
  const messageInput = document.getElementById("messageInput");
  const explicitMessage = typeof options.message === "string";
  const message = explicitMessage
    ? options.message.trim()
    : messageInput.value.trim();

  if (!message || isTyping || !isInitialized) return;

  if (!explicitMessage) {
    messageInput.value = "";
    // Collapses the box AND clears .is-multiline, so the controls return inline.
    applyComposerLayout(messageInput);
  }

  updateSendButtonState();

  if (options.displayUserMessage !== false) {
    addMessage(message, "user");
  }

  // GID-220: files are read in the background now, so a question can overtake
  // them. Send anyway, but say so once for the whole batch.
  const stillReading = stillReadingNotice(attachedFiles);
  if (stillReading) addMessage(stillReading, "system");

  // Start this request's feed empty, so it never shows the previous answer's
  // trail while the new one is still being routed.
  activityLog.clear();
  showTypingIndicator();

  const plannedTargetFlagField = getPlannedTargetFlagField();
  const routeOverrideSelect = document.getElementById("routeOverrideSelect");
  // Reads the route and returns the selector to Auto, so a manual route applies
  // to this message only. See routeSelector.js for why it resets on send rather
  // than on completion.
  const routeOverride = takeRouteOverride(routeOverrideSelect);
  // takeRouteOverride writes .value directly, which fires no change event, so
  // the listbox label has to be told the route went back to Auto.
  routeOverrideListbox?.sync();
  const editHarnessModeSelect = document.getElementById("editHarnessModeSelect");
  const editHarnessMode = editHarnessModeSelect?.value || DEFAULT_EDIT_HARNESS_MODE;

  // GID-106: bind this query's rendering to the session it belongs to, so a
  // mid-answer session (or project) switch stops rendering into the
  // now-visible (wrong) session. The request is not aborted; the answer
  // persists to its origin session and shows on return. The origin is bound
  // eagerly via onSessionBound (called by chatService synchronously, before
  // any await can yield to a switch) rather than lazily on the first chunk —
  // see streamSessionGuard.js for why. Decision logic is unit-tested there.
  const streamGuard = createStreamSessionGuard(() => chatService.currentSessionId);

  try {
    const response = await chatService.sendMessage(message, {
      stream: true,
      plannedFilterFlagField: plannedTargetFlagField,
      routeOverride,
      editHarnessMode,
      comparisonAction: options.comparisonAction,
      // The user chose "Send with plan from …" on a not-sent notice.
      allowStalePlan: options.allowStalePlan === true,
      // A fresh read normally lands in well under a second; only a wait past
      // ~1s gets a line in the feed, so quick sends show nothing new.
      onPlanStatus: (status) => {
        try {
          if (!streamGuard.shouldRender()) return;
          const text = status === "waiting"
            ? "Reading your plan from Microsoft Project…"
            : "Read your plan from Microsoft Project.";
          if (status === "fresh" && !activityLog.entries().some((e) => e.id === "plan_read")) return;
          if (activityLog.add({ stream_event: "plan_read", message: text })) activityStrip.render();
        } catch (error) {
          console.error("Activity feed failed to show the plan read:", error);
        }
      },
      onSessionBound: (sessionId) => streamGuard.bindOrigin(sessionId),
      onChunk: (_chunk, fullResponse) => {
        if (streamGuard.shouldRender()) {
          updateStreamingMessage(fullResponse);
        }
      },
      onComplete: (data, fullResponse) => {
        // Before hiding the strip: a short answer can finish without ever
        // emitting a progress event, so the final metadata is the only place
        // the route and model appear.
        activityLog.observeMetadata(data?.metadata);
        hideTypingIndicator();
        // Switched away mid-answer means do not render into the wrong session:
        // the message is saved to its origin session and shows on return. The
        // strip still has to come down either way, so this is a branch rather
        // than an early return.
        if (streamGuard.shouldRender(data?.session_id)) {
          const replyContent = document.querySelector(".streaming-message")
            ? finalizeStreamingMessage()
            // The live element was wiped by a switch-away-and-back; render the
            // completed answer fresh into the (origin) session now in view.
            : addMessage(fullResponse, "bot");
          // GID-83: a failed turn has a trace too, and it is the one to read
          // beside the error; attachRatingControls skips such turns.
          setSentryChatContext({
            sessionId: data?.session_id || chatService.currentSessionId,
            traceId: data?.metadata?.langfuse_trace_id,
          });
          // A failed turn ends with an error chunk: nothing there to rate.
          if (!data?.metadata?.error) attachRatingControls(replyContent, {
            sessionId: data?.session_id || chatService.currentSessionId,
            messageId: data?.message_id,
            traceId: data?.metadata?.langfuse_trace_id,
          });
          attachRunSummary();
        }
        hideActivityStrip();
      },
      onEvent: (data) => {
        // GID-202: progress is shown live and then thrown away. It used to be
        // appended to the transcript as a permanent "system" message, which
        // left the conversation littered with notices about work that had
        // already finished.
        //
        // The try/catch is not decoration: processStreamData wraps its dispatch
        // in a catch that only console.warns, so a render bug in here fails
        // silently and looks like "the backend sent nothing". Report it.
        try {
          if (!streamGuard.shouldRender()) return;
          if (activityLog.add(data?.metadata)) {
            activityStrip.render();
          }
        } catch (error) {
          console.error("Activity feed failed to render a progress event:", error, data?.metadata);
        }
      }
    });

    // GID-107: handleResponseMetadata renders its own messages (Download XML
    // button, "Ribbon Sync is ready", classification/filter status) straight
    // into #chatMessages — including from async .then()/.catch() callbacks
    // (e.g. postPendingSync, applyTextFieldLabelsWithSnapshot) that can fire
    // well AFTER the user has switched sessions, independent of the
    // onChunk/onComplete guards above. The real side effects in there (bridge
    // sync registration, MS Project field writes) still belong to whatever
    // document is actually open and must always run — only the chat MESSAGE
    // output needs to check "is the origin session still in view", checked
    // fresh at the moment each message would actually render, not just once
    // up front. handleResponseMetadata takes that live check as a callback.
    if (response && response.metadata) {
      handleResponseMetadata(response.metadata, () => streamGuard.shouldRender(response.session_id));
    } else {
      chatService.clearPendingFilterSyncPrefetch();
    }

    if (response && response.token_usage && streamGuard.shouldRender(response.session_id)) {
      displayTokenUsage(response.token_usage);
    }
  } catch (error) {
    hideTypingIndicator();
    finalizeStreamingMessage();
    // A stopped answer is still an answer from some route, and the partial text
    // stays on screen — so it gets the same attribution a finished one does.
    // This must run before hideActivityStrip(), which clears what it reads.
    attachRunSummary();
    // The feed itself goes: it describes work that is no longer happening.
    hideActivityStrip();
    chatService.clearPendingFilterSyncPrefetch();

    if (error.type === "abort_error") {
      return;
    }

    // GID-106: if the user switched sessions mid-request, don't surface this
    // error in the session they are now viewing. But if the request never
    // even reached dispatch (e.g. it threw while ensuring the session,
    // before onSessionBound could bind an origin), there is no "wrong
    // session" to guard against — always show the error, or it silently
    // vanishes with no feedback (GID-107).
    if (streamGuard.isBound() && !streamGuard.shouldRender()) {
      return;
    }

    if (error.type === "plan_not_fresh") {
      // Nothing was sent: there was no plan read for this message. Say why and
      // let the user retry or knowingly send on the older plan.
      showStalePlanNotice(message, options, error);
    } else if (error.type === "terms_required") {
      // Nothing reached the model. Put the text back so it can be sent as-is
      // once the terms screen (raised by api.js) has been accepted.
      if (!explicitMessage && !messageInput.value) {
        messageInput.value = message;
        applyComposerLayout(messageInput);
        updateSendButtonState();
      }
      addMessage(
        "This message was not sent. Accept the updated Terms of Use, then send it again.",
        "bot"
      );
    } else if (error.message && error.message.includes("Chat service not initialized")) {
      addMessage("I'm currently offline, but I can still help you with basic tasks. Please try again later.", "bot");
    } else if (error.type === "network_error") {
      addMessage("Network connection error. Please check your internet connection and try again.", "bot");
    } else if (error.type === "rate_limit") {
      const retryAfter = Number(error.retryAfterSeconds || error.details?.retry_after_seconds || 30);
      const waitSeconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 30;
      addMessage(`You've reached the limit of 3 requests per minute. Please wait ${waitSeconds} seconds and try again.`, "bot");
    } else if (error.type === "heavy_load") {
      addMessage("Heavy load on model side. Please wait a few moments and try again.", "bot");
    } else if (error.type === "server_error") {
      addMessage(serverErrorMessage(error), "bot");
    } else if (error.type === "timeout_error") {
      addMessage("Request timed out. Please try again with a shorter message.", "bot");
    } else {
      handleChatError(error);
    }
  } finally {
    // GID-202: the feed MUST come down on every exit, and two of them did not
    // reach the calls above. api.js returns normally when the stream ends
    // without a [DONE] marker but did deliver data ("Stream completed
    // successfully"), so onComplete never fires and nothing throws -- a backend
    // dying mid-stream, or a proxy cutting the connection, left the feed on
    // screen under the finished answer with its clock still ticking, and leaked
    // the 1s interval behind it, one per request for the life of the pane.
    //
    // Both calls are idempotent: attachRunSummary() returns early once a
    // .message-attribution exists, and teardown() null-checks its element and
    // ticker. So the prompt teardown in onComplete still happens at the usual
    // moment and this only catches what it misses. The shouldRender() guard
    // matches onComplete's: without it a summary could be pinned to whichever
    // message is last in a session the user switched to mid-request.
    if (streamGuard.shouldRender()) {
      attachRunSummary();
    }
    hideActivityStrip();
  }
}

// navigator.clipboard is only defined in a SECURE context (HTTPS or localhost).
// The test env is served over plain HTTP, where navigator.clipboard is undefined,
// so `navigator.clipboard.writeText(...)` threw synchronously and copy did nothing.
// Guard it and fall back to the legacy textarea + execCommand path for insecure origins.
function copyTextToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).catch(() => legacyCopyText(text));
    return;
  }
  legacyCopyText(text);
}

function legacyCopyText(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  } catch (e) {
    /* best-effort — clipboard unavailable */
  }
}

function makeCopyButton(getText) {
  const copyBtn = document.createElement("button");
  copyBtn.className = "copy-button";
  copyBtn.title = "Copy message";
  copyBtn.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
  copyBtn.addEventListener("click", () => {
    copyTextToClipboard(getText());
    copyBtn.textContent = "✓";
    setTimeout(() => {
      copyBtn.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
    }, 1500);
  });
  return copyBtn;
}

function showStalePlanNotice(message, options, error) {
  const chatMessages = document.getElementById("chatMessages");
  const resend = (extra) => sendMessage({
    ...options,
    message,
    displayUserMessage: false,
    ...extra,
  });
  const notice = buildStalePlanNotice(document, {
    reason: error.reason,
    connectorSeenSecondsAgo: error.connectorSeenSecondsAgo,
    planReadAt: error.planReadAt,
    waitedMs: error.waitedMs,
    onRetry: () => resend({ allowStalePlan: false }),
    onSendAnyway: () => resend({ allowStalePlan: true }),
  });
  chatMessages.appendChild(notice);
  chatMessages.scrollTop = chatMessages.scrollHeight;
}

function addMessage(text, sender) {
  const chatMessages = document.getElementById("chatMessages");


  const messageDiv = document.createElement("div");
  messageDiv.className = `message ${sender}-message`;

  const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  let formattedText = text;
  if (sender === "system") {
    formattedText = escapeHtml(text).replace(/\n/g, "<br>");
  } else {
    formattedText = renderMarkdownSafely(text);
  }

  messageDiv.innerHTML = `
    <div class="message-avatar">
      <img src="../../assets/logo-filled.png" alt="${sender === "user" ? "You" : sender === "system" ? "System" : "Bot"}" />
    </div>
    <div class="message-content">
      <div class="message-text">${formattedText}</div>
      <div class="message-time">${time}</div>
    </div>
  `;

  const messageText = messageDiv.querySelector(".message-text");
  if (isMostlyHebrew(text)) {
    messageText.dir = "rtl";
  }

  if (sender !== "system") {
    const copyBtn = makeCopyButton(() => messageText.innerText);
    messageDiv.querySelector(".message-content").appendChild(copyBtn);
  }

  chatMessages.appendChild(messageDiv);
  if (sender === "bot") {
    renderTableDownloadSection(messageDiv.querySelector(".message-content"));
  }
  chatMessages.scrollTop = chatMessages.scrollHeight;

  chatHistory.push({ text, sender, time });
  return messageDiv.querySelector(".message-content");
}


// GID-202: what the pane is doing right now, for the life of one request.
// Cleared on send so a new question never shows the previous one's trail.
const activityLog = createActivityLog();
const activityStrip = createActivityStripView(activityLog);

/**
 * Leave the route and model attached to the finished answer.
 *
 * The live feed goes when the request ends, so without this the user could
 * never look back and see which route produced a reply — which is exactly when
 * they want to know, i.e. when the reply looks wrong.
 */
function attachRunSummary() {
  const summary = activityLog.summary();
  if (!summary) return;

  const chatMessages = document.getElementById("chatMessages");
  const content = getLatestBotMessageContent(chatMessages);
  if (!content || content.querySelector(".message-attribution")) return;

  const line = document.createElement("div");
  line.className = "message-attribution";
  line.textContent = summary;
  content.appendChild(line);
}

/** Take the feed down once the request it describes is over. */
function hideActivityStrip() {
  activityLog.clear();
  activityStrip.teardown();
}

function showTypingIndicator() {
  isTyping = true;
  const chatMessages = document.getElementById("chatMessages");
  const typingDiv = document.createElement("div");
  typingDiv.id = "typing-indicator";
  typingDiv.className = "message bot-message";

  typingDiv.innerHTML = `
    <div class="message-avatar">
      <img src="../../assets/logo-filled.png" alt="Bot" />
    </div>
    <div class="message-content">
      <div class="message-text typing-dots">
        <span></span><span></span><span></span>
      </div>
    </div>
  `;

  chatMessages.appendChild(typingDiv);
  activityStrip.mountInto(typingDiv.querySelector(".message-content"));
  chatMessages.scrollTop = chatMessages.scrollHeight;

  const sendButton = document.getElementById("sendButton");
  const stopButton = document.getElementById("stopButton");
  if (sendButton) sendButton.style.display = "none";
  if (stopButton) stopButton.style.display = "flex";
  updateSendButtonState();
}

function hideTypingIndicator() {
  isTyping = false;
  const typingIndicator = document.getElementById("typing-indicator");
  if (typingIndicator) {
    typingIndicator.remove();
  }

  const sendButton = document.getElementById("sendButton");
  const stopButton = document.getElementById("stopButton");
  if (sendButton) sendButton.style.display = "";
  if (stopButton) stopButton.style.display = "none";

  updateSendButtonState();
}

function updateStatus(text, status) {
  const statusText = document.querySelector(".status-text");
  const statusDot = document.querySelector(".status-dot");

  if (statusText) statusText.textContent = text;

  if (statusDot) {
    statusDot.className = "status-dot";
    statusDot.classList.add(status);
  }
}

function showErrorMessage(message) {
  const chatMessages = document.getElementById("chatMessages");
  const errorDiv = document.createElement("div");
  errorDiv.className = "message bot-message error-message";

  errorDiv.innerHTML = `
    <div class="message-avatar">
      <img src="../../assets/logo-filled.png" alt="Bot" />
    </div>
    <div class="message-content">
      <div class="message-text">
        <strong>Error:</strong> ${escapeHtml(message)}
      </div>
      <div class="message-time">${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
    </div>
  `;

  chatMessages.appendChild(errorDiv);
  chatMessages.scrollTop = chatMessages.scrollHeight;
}

function handleChatError(error) {
  console.error("Chat error:", error);

  let errorMessage = ERROR_MESSAGES.unknown;

  if (error.type === "network_error") {
    errorMessage = ERROR_MESSAGES.network;
    updateStatus("Offline", "offline");
  } else if (error.type === "server_error") {
    errorMessage = serverErrorMessage(error);
  } else if (error.message) {
    errorMessage = error.message;
  }

  showErrorMessage(errorMessage);

  finalizeStreamingMessage();
}

function updateStreamingMessage(fullResponse) {
  const chatMessages = document.getElementById("chatMessages");
  const lastMessage = chatMessages.lastElementChild;

  if (lastMessage && lastMessage.classList.contains("streaming-message")) {
    const messageText = lastMessage.querySelector(".message-text");
    lastMessage._rawText = fullResponse;
    if (messageText) {
      messageText.innerHTML = renderMarkdownSafely(fullResponse);
      messageText.classList.add("streaming");
      messageText.dir = isMostlyHebrew(fullResponse) ? "rtl" : "";
    }
  } else {
    const typingIndicator = document.getElementById("typing-indicator");
    if (typingIndicator) {
      typingIndicator.remove();
    }

    const messageDiv = document.createElement("div");
    messageDiv.className = "message bot-message streaming-message";
    messageDiv.id = `streaming-message-${Date.now()}`;

    const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    messageDiv.innerHTML = `
      <div class="message-avatar">
        <img src="../../assets/logo-filled.png" alt="Bot" />
      </div>
      <div class="message-content">
        <div class="message-text streaming"></div>
        <div class="message-time">${time}</div>
      </div>
    `;

    messageDiv._rawText = fullResponse;
    const newMessageText = messageDiv.querySelector(".message-text");
    if (newMessageText) {
      newMessageText.innerHTML = renderMarkdownSafely(fullResponse);
      if (isMostlyHebrew(fullResponse)) {
        newMessageText.dir = "rtl";
      }
    }

    chatMessages.appendChild(messageDiv);
    // Carry the feed across: the typing indicator was just removed above.
    activityStrip.mountInto(messageDiv.querySelector(".message-content"));
  }

  chatMessages.scrollTop = chatMessages.scrollHeight;
}

function finalizeStreamingMessage() {
  const chatMessages = document.getElementById("chatMessages");

  const streamingMessage = document.querySelector(".streaming-message");
  if (streamingMessage) {
    const messageText = streamingMessage.querySelector(".message-text");
    if (messageText) {
      const finalText = streamingMessage._rawText || messageText.textContent || "";
      messageText.innerHTML = renderMarkdownSafely(finalText);
      messageText.classList.remove("streaming");
      messageText.dir = isMostlyHebrew(finalText) ? "rtl" : "";
    }
    streamingMessage.classList.remove("streaming-message");

    const msgContent = streamingMessage.querySelector(".message-content");
    if (msgContent) {
      renderTableDownloadSection(msgContent);
      const copyBtn = makeCopyButton(() => messageText.innerText);
      msgContent.appendChild(copyBtn);
    }
    return msgContent;
  }
  return null;
}

/**
 * 👍 / 👎 under a reply (GID-83). Needs the reply's session; its message id
 * and Langfuse trace id tie the rating to the exact turn when known.
 */
// The newest reply shown, so "Report a problem" can name the turn the user
// was looking at. Replies render oldest first, so the last one seen wins.
let latestReply = { sessionId: null, traceId: null };

function attachRatingControls(contentEl, { sessionId, messageId, traceId }) {
  if (!contentEl || !sessionId || contentEl.querySelector(".feedback-controls")) return;
  latestReply = { sessionId, traceId: traceId || null };
  // GID-83: every later pane event names this turn's trace.
  setSentryChatContext(latestReply);
  contentEl.appendChild(buildRatingControls(document, {
    sessionId,
    messageId,
    traceId,
    send: (body) => chatAPI.sendFeedback(body),
  }));
}

function openProblemReport() {
  mountProblemReport(document, {
    getSessionId: () => chatService.currentSessionId || null,
    getTraceId: () => (latestReply.sessionId === chatService.currentSessionId ? latestReply.traceId : null),
    send: (body) => chatAPI.sendFeedback(body),
  });
}

const ALLOWED_MARKDOWN_TAGS = new Set([
  "P",
  "BR",
  "STRONG",
  "EM",
  "UL",
  "OL",
  "LI",
  "CODE",
  "PRE",
  "BLOCKQUOTE",
  "A",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "TABLE",
  "THEAD",
  "TBODY",
  "TR",
  "TH",
  "TD",
]);

const STRIP_WITH_CONTENT_TAGS = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED"]);

function renderMarkdownSafely(text) {
  const renderedHtml = marked.parse(text || "", { breaks: true, gfm: true });
  return sanitizeRenderedMarkdown(renderedHtml);
}

function sanitizeRenderedMarkdown(html) {
  const template = document.createElement("template");
  template.innerHTML = html;
  sanitizeNodeTree(template.content);
  return template.innerHTML;
}

function sanitizeNodeTree(root) {
  const children = Array.from(root.childNodes);
  children.forEach((child) => {
    if (child.nodeType === Node.COMMENT_NODE) {
      child.remove();
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const tagName = child.tagName.toUpperCase();

    if (STRIP_WITH_CONTENT_TAGS.has(tagName)) {
      child.remove();
      return;
    }

    if (!ALLOWED_MARKDOWN_TAGS.has(tagName)) {
      sanitizeNodeTree(child);
      while (child.firstChild) {
        child.parentNode.insertBefore(child.firstChild, child);
      }
      child.remove();
      return;
    }

    sanitizeElementAttributes(child);
    sanitizeNodeTree(child);
  });
}

function sanitizeElementAttributes(element) {
  Array.from(element.attributes).forEach((attr) => {
    const attrName = attr.name.toLowerCase();

    if (attrName.startsWith("on")) {
      element.removeAttribute(attr.name);
      return;
    }

    if (element.tagName.toUpperCase() === "A") {
      if (!["href", "title", "target", "rel"].includes(attrName)) {
        element.removeAttribute(attr.name);
        return;
      }

      if (attrName === "href" && !isSafeMarkdownHref(attr.value)) {
        element.removeAttribute(attr.name);
        return;
      }
    } else {
      element.removeAttribute(attr.name);
      return;
    }
  });

  if (element.tagName.toUpperCase() === "A") {
    element.setAttribute("target", "_blank");
    element.setAttribute("rel", "noopener noreferrer");
  }
}

function isSafeMarkdownHref(href) {
  if (!href) {
    return false;
  }

  const trimmedHref = href.trim();
  if (trimmedHref.startsWith("#")) {
    return true;
  }

  try {
    const url = new URL(trimmedHref, window.location.origin);
    return ["http:", "https:", "mailto:"].includes(url.protocol);
  } catch (error) {
    return false;
  }
}

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

// The bubble reads right-to-left only when it is MOSTLY Hebrew. "Any Hebrew
// letter" flipped English answers that merely quote a Hebrew calendar or task
// name, which moved their full stops to the front of the line. Mixed messages
// still read correctly line by line: see unicode-bidi: plaintext in taskpane.css.
function isMostlyHebrew(text) {
  const value = String(text || "");
  const hebrew = (value.match(/\p{Script=Hebrew}/gu) || []).length;
  const latin = (value.match(/\p{Script=Latin}/gu) || []).length;
  return hebrew > latin;
}

async function displayProjectInfo() {
  try {
    const projectSummary = chatService.getProjectSummary();

    if (!projectSummary) {
      return;
    }

    let projectInfoElement = document.getElementById("projectInfo");
    if (!projectInfoElement) {
      projectInfoElement = document.createElement("div");
      projectInfoElement.id = "projectInfo";
      projectInfoElement.className = "project-info active-document";

      const header = document.querySelector(".header");
      if (header) {
        header.insertAdjacentElement("afterend", projectInfoElement);
      }
    } else {
      projectInfoElement.className = "project-info active-document";
    }

    const infoHtml = `
      <div class="project-info-content">
        <h4>${escapeHtml(projectSummary.name)} <span class="document-status">ACTIVE</span></h4>
        <div class="project-stats">
          <span class="stat">${projectSummary.total_tasks} tasks</span>
          <span class="stat">${projectSummary.total_resources} resources</span>
          <span class="stat">${projectSummary.critical_tasks} critical</span>
          <span class="stat">${projectSummary.completed_tasks} completed</span>
        </div>
        ${projectSummary.percent_complete > 0
        ? `<div class="progress-bar">
            <div class="progress-fill" style="width: ${projectSummary.percent_complete}%"></div>
            <span class="progress-text">${projectSummary.percent_complete}% complete</span>
          </div>`
        : ""
      }
        ${projectSummary.overdue_tasks > 0
        ? `<div class="warning">${projectSummary.overdue_tasks} overdue tasks</div>`
        : ""
      }
      </div>
    `;

    projectInfoElement.innerHTML = infoHtml;
  } catch (error) {
    console.error("Failed to display project information:", error);
  }
}

async function handleDocumentChange(changeInfo) {
  try {
    usedFilterFlagFields.clear();
    nextFilterFlagIndex = 1;
    chatService.clearPendingFilterSyncPrefetch();

    const notification = document.createElement("div");
    notification.className = "document-change-notification";
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">Document</span>
        <span class="notification-text">
          Switched to: <strong>${escapeHtml(changeInfo.currentDocument.name)}</strong>
        </span>
      </div>
    `;

    const chatContainer = document.querySelector(".chat-container");
    if (chatContainer) {
      chatContainer.insertBefore(notification, chatContainer.firstChild);

      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 5000);
    }

    await displayProjectInfo();

    addMessage(`Project switched to: ${changeInfo.currentDocument.name}`, "system");
  } catch (error) {
    console.error("Error handling document change in UI:", error);
  }
}

async function getProjectInsights() {
  try {
    const projectSummary = chatService.getProjectSummary();

    if (!projectSummary) {
      return "No project information available. Please refresh the project data.";
    }

    let insights = `**Current Project: ${projectSummary.name}**\n\n`;
    insights += `**Project Overview:**\n`;
    insights += `- Total Tasks: ${projectSummary.total_tasks}\n`;
    insights += `- Total Resources: ${projectSummary.total_resources}\n`;
    insights += `- Completion: ${projectSummary.percent_complete}%\n`;
    insights += `- Critical Tasks: ${projectSummary.critical_tasks}\n`;
    insights += `- Completed Tasks: ${projectSummary.completed_tasks}\n`;

    if (projectSummary.overdue_tasks > 0) {
      insights += `- Overdue Tasks: ${projectSummary.overdue_tasks}\n`;
    }

    if (projectSummary.start_date && projectSummary.finish_date) {
      insights += `- Start Date: ${new Date(projectSummary.start_date).toLocaleDateString()}\n`;
      insights += `- Finish Date: ${new Date(projectSummary.finish_date).toLocaleDateString()}\n`;
    }

    if (projectSummary.total_cost > 0) {
      insights += `- Total Cost: $${projectSummary.total_cost.toLocaleString()}\n`;
    }

    if (projectSummary.total_work > 0) {
      insights += `- Total Work: ${projectSummary.total_work} hours\n`;
    }

    insights += `\n*Last updated: ${new Date(projectSummary.last_updated).toLocaleString()}*`;

    return insights;
  } catch (error) {
    console.error("Failed to get project insights:", error);
    return "Unable to retrieve project insights at this time.";
  }
}

async function checkOfficeEnvironment() {
  try {
    const environment = {
      officeAvailable: !!Office,
      contextAvailable: !!Office?.context,
      application: Office?.context?.application,
      host: Office?.context?.host,
      platform: Office?.context?.platform,
      documentAvailable: !!Office?.context?.document,
      documentUrl: Office?.context?.document?.url,
    };

    const chatMessages = document.getElementById("chatMessages");
    if (chatMessages) {
      const infoDiv = document.createElement("div");
      infoDiv.className = "message bot-message";
      infoDiv.innerHTML = `
        <div class="message-content">
          <div class="message-text">
            <strong>Office Environment Check:</strong><br>
            Office Available: ${environment.officeAvailable ? "Yes" : "No"}<br>
            Context Available: ${environment.contextAvailable ? "Yes" : "No"}<br>
            Application: ${environment.application || "Unknown"}<br>
            Host: ${environment.host || "Unknown"}<br>
            Platform: ${environment.platform || "Unknown"}<br>
            Document Available: ${environment.documentAvailable ? "Yes" : "No"}<br>
            Document URL: ${environment.documentUrl || "None"}
          </div>
        </div>
      `;
      chatMessages.appendChild(infoDiv);
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    return environment;
  } catch (error) {
    console.error("Failed to check Office environment:", error);
    return { error: error.message };
  }
}

/**
 * Test Local MS Project API
 */
async function testLocalAPI() {
  try {
    addMessage("Testing Local MS Project API...", "system");

    const projectData = await msProjectService.getProjectData();

    if (!projectData) {
      addMessage("No project data available", "system");
      return;
    }

    let unifiedMessage = `Local MS Project API Test Results:\n\n`;

    const fileInfo = projectData.fileInfo;
    unifiedMessage += `Project Information:\n`;
    unifiedMessage += `   Project Name: ${fileInfo.name}\n`;
    unifiedMessage += `   Document Path: ${fileInfo.documentUrl}\n`;
    unifiedMessage += `   Project ID: ${projectData.project_id}\n`;
    unifiedMessage += `   Tasks: ${projectData.tasks.length}\n`;
    unifiedMessage += `   Resources: ${projectData.resources.length}\n`;
    unifiedMessage += `   Assignments: ${projectData.assignments.length}\n\n`;

    if (projectData.tasks.length > 0) {
      const sampleTask = projectData.tasks[0];
      unifiedMessage += `Sample Task (${sampleTask.name}):\n`;
      unifiedMessage += `   Start: ${sampleTask.start || "N/A"}\n`;
      unifiedMessage += `   Finish: ${sampleTask.finish || "N/A"}\n`;
      unifiedMessage += `   Duration: ${sampleTask.duration}\n`;
      unifiedMessage += `   Progress: ${sampleTask.percentComplete}%\n`;
      unifiedMessage += `   Critical: ${sampleTask.critical ? "Yes" : "No"}\n`;
      unifiedMessage += `   Resources: ${sampleTask.resourceNames || "None"}\n\n`;
    }

    unifiedMessage += `Local API test successful! Retrieved project data for: ${fileInfo.name}`;

    addMessage(unifiedMessage, "system");

    return {
      projectId: projectData.project_id,
      tasks: projectData.tasks.length,
      resources: projectData.resources.length,
      assignments: projectData.assignments.length,
      sampleTask: projectData.tasks[0],
    };
  } catch (error) {
    console.error("Local API test failed:", error);
    addMessage(`Local API test failed: ${error.message}`, "system");
  }
}

async function testGetSelectedView() {
  try {
    console.log("Testing Office.context.document.getSelectedViewAsync...");

    if (!Office || !Office.context || !Office.context.document) {
      console.log("Office context not available");
      return;
    }

    Office.context.document.getSelectedViewAsync((result) => {
      console.log("getSelectedViewAsync result:", result);

      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const viewType = result.value.viewType;
        const viewName = result.value.viewName;

        console.log(`Active view: ${viewName} (${viewType})`);

        switch (viewType) {
          case 1:
            viewManagementService.handleGanttView();
            break;
          case 3:
            viewManagementService.handleResourceSheet();
            break;
          case 4:
            viewManagementService.handleResourceUsage();
            break;
          case 0:
            viewManagementService.handleTaskSheet();
            break;
          default:
            console.log("Unsupported or unadapted view.");
            break;
        }
      } else {
        console.error("Error identifying view:", result.error.message);
      }
    });
  } catch (error) {
    console.error("Error in testGetSelectedView:", error);
  }
}

function getLatestBotMessageContent(chatMessages) {
  if (!chatMessages) return null;

  const botMessages = Array.from(
    chatMessages.querySelectorAll(".message.bot-message:not(#typing-indicator)")
  );

  for (let i = botMessages.length - 1; i >= 0; i -= 1) {
    const messageContent = botMessages[i].querySelector(".message-content");
    if (messageContent) return messageContent;
  }

  return null;
}

/**
 * @param {Object} metadata
 * @param {() => boolean} [canRenderNow] - GID-107: re-checked at the moment
 *   each message would actually render (not captured once up front), since
 *   several branches below only add their message inside a .then()/.catch()
 *   after a real network round-trip — by which time the user may have
 *   switched away from the session this response belongs to. Defaults to
 *   always-true for callers (tests, other call sites) that don't need the
 *   origin-session check.
 */
// One per task pane: the full rebuild explanation is shown once per chat.
const uidNoticeTracker = createUidNoticeTracker();

function handleResponseMetadata(metadata, canRenderNow = () => true) {
  const safeAddMessage = (text, role) => {
    if (canRenderNow()) addMessage(text, role);
  };
  console.log("Response metadata:", metadata);
  // Used by the filtering branch near the end of this function.
  const route = metadata?.route || null;
  const pendingFilterSyncPrefetch = chatService.getPendingFilterSyncPrefetch();
  const stagedFieldWriteback =
    metadata?.field_writeback?.mode === "vsto_sync" &&
    metadata?.field_writeback?.status === "staged";
  const fieldWritebackReadyMessage = () => {
    const operation = metadata.field_writeback.operation === "classification"
      ? "Classification"
      : "Filter";
    const field = metadata.field_writeback.field || "the selected field";
    return `${operation} prepared in ${field}. Click **Novatime AI > Sync** in the Project ribbon to apply it.`;
  };

  if (metadata?.bridge_sync?.command_id) {
    // Surface backend resolution warnings (dropped dependency references,
    // orphaned tasks) BEFORE the ready notice — silent data loss during
    // staging must be visible to the user, not just in server logs.
    const syncWarnings = metadata?.bridge_sync?.warnings;
    if (Array.isArray(syncWarnings) && syncWarnings.length > 0) {
      safeAddMessage(
        "⚠ Sync prepared with warnings:\n- " + syncWarnings.join("\n- "),
        "bot"
      );
    }
    // Register the sync under the session's STABLE project guid (no live
    // name re-fetch -> no drift race). The VSTO ribbon Sync button later
    // resolves the active project's guid the same way and fetches by it.
    const payload = buildPendingSyncPayload(chatService.activeProject, metadata.bridge_sync);
    if (!payload) {
      console.warn("Pending sync not registered: missing project guid or command_id", {
        guid: chatService.activeProject?.guid,
        bridge_sync: metadata?.bridge_sync,
      });
      safeAddMessage("Ribbon Sync could not be prepared for this project. Use **Download XML** instead.", "bot");
    } else {
      projectsAPI.postPendingSync(payload.project_guid, payload.command_id, payload.command_hash).then(() => {
        console.log("Pending sync posted to backend:", payload);
        if (!stagedFieldWriteback) {
          safeAddMessage(
            "✓ Ribbon Sync is ready — click **Novatime AI > Sync** in the Project ribbon to apply.",
            "bot"
          );
        }
        // GID-261: say when this sync gives tasks new Unique IDs.
        const uidNotice = uidNoticeTracker.textFor(
          metadata.bridge_sync.uid_notice,
          chatService.currentSessionId || null
        );
        if (uidNotice) safeAddMessage(uidNotice, "bot");
      }).catch((error) => {
        console.error("Pending sync post failed:", error);
        safeAddMessage("Ribbon Sync could not be prepared. Use **Download XML** instead.", "bot");
      });
    }
  } else if (staleSyncNotice(metadata)) {
    safeAddMessage(staleSyncNotice(metadata), "bot");
  } else if (isNoChangesSync(metadata)) {
    // The edit changed nothing and nothing was staged; the reply says so.
  } else if (metadata?.gantt_generation && !metadata?.bridge_sync?.command_id) {
    console.warn("Bridge sync not available in response metadata:", metadata?.bridge_sync);
    safeAddMessage(
      "Ribbon Sync is unavailable for this generation. Use **Download XML** to get the project file.",
      "bot"
    );
  }

  if (stagedFieldWriteback) {
    chatService.clearPendingFilterSyncPrefetch();
    if (!metadata?.bridge_sync?.command_id) {
      safeAddMessage(fieldWritebackReadyMessage(), "system");
    }
    return;
  }

  // GID-107: both blocks below mutate whatever bot message is CURRENTLY
  // last in #chatMessages via getLatestBotMessageContent() — with no
  // session check, that's whatever the user is now viewing, not
  // necessarily this response's own message. If the user switched away,
  // this would append a download/compare button onto an unrelated (or
  // brand-new) session's message. Gate on canRenderNow() same as
  // safeAddMessage — this runs synchronously here, so a single check
  // up front is enough (no async gap to re-check across).
  const downloadInfo = canRenderNow() ? extractDownloadInfo(metadata) : null;
  if (downloadInfo) {
    const chatMessages = document.getElementById("chatMessages");
    const messageContent = getLatestBotMessageContent(chatMessages);

    if (messageContent) {
      if (messageContent.querySelector(".download-section")) {
        // Already rendered for this response (e.g. duplicate metadata
        // handling) — nothing left to do.
        chatService.clearPendingFilterSyncPrefetch();
        return;
      }
      renderDownloadSection(messageContent, downloadInfo);
    } else {
      console.warn("No bot message content found for download/sync controls");
    }
  }

  if (canRenderNow()) {
    const toolCallSummary = extractToolCallSummary(metadata);
    if (toolCallSummary) {
      const chatMessages = document.getElementById("chatMessages");
      const messageContent = getLatestBotMessageContent(chatMessages);
      if (messageContent) {
        renderToolCallSummary(messageContent, toolCallSummary);
      }
    }
  }

  if (canRenderNow() && metadata?.has_compare_export && metadata?.compare_export_url) {
    const chatMessages = document.getElementById("chatMessages");
    const messageContent = getLatestBotMessageContent(chatMessages);
    if (messageContent && !messageContent.querySelector(".compare-download-section")) {
      const compareDownloadUrl = metadata.compare_export_url;
      const comparisonId = metadata.comparison_id || "comparison";
      const compareButtonSection = document.createElement("div");
      compareButtonSection.className = "download-section compare-download-section";
      compareButtonSection.innerHTML = `
        <div class="download-button-container">
          <button type="button" class="download-button compare-download-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7,10 12,15 17,10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download full comparison CSV
          </button>
        </div>
      `;
      messageContent.appendChild(compareButtonSection);

      const compareDownloadBtn = compareButtonSection.querySelector(".compare-download-button");
      if (compareDownloadBtn) {
        compareDownloadBtn.addEventListener("click", async () => {
          const originalText = compareDownloadBtn.innerHTML;
          try {
            compareDownloadBtn.disabled = true;
            compareDownloadBtn.innerHTML = "Downloading...";

            const { blob, filename } = await filesAPI.downloadFile(
              compareDownloadUrl,
              `schedule-comparison-${comparisonId}.csv`
            );
            const objectUrl = globalThis.URL.createObjectURL(blob);
            const tempAnchor = document.createElement("a");
            tempAnchor.href = objectUrl;
            tempAnchor.download = filename || `schedule-comparison-${comparisonId}.csv`;
            tempAnchor.style.display = "none";
            document.body.appendChild(tempAnchor);
            tempAnchor.click();
            document.body.removeChild(tempAnchor);
            globalThis.setTimeout(() => globalThis.URL.revokeObjectURL(objectUrl), 1000);

            compareDownloadBtn.disabled = false;
            compareDownloadBtn.innerHTML = originalText;
          } catch (err) {
            console.error("Comparison CSV download error:", err);
            safeAddMessage(`Comparison CSV download failed: ${err.message || "authentication or file access error"}`, "bot");
            compareDownloadBtn.disabled = false;
            compareDownloadBtn.innerHTML = originalText;
          }
        });
      }
    }
  }

  if (metadata.empty_gantt_generation) {
    console.log("Empty Gantt generation completed");
  }

  // Handle multi-class filtering / classification labels.
  if (metadata.gantt_data && metadata.gantt_data.classified) {
    console.log("Received Classified View Data");
    chatService.clearPendingFilterSyncPrefetch();
    const classificationField = metadata.classification_field || "Text28";
    const assignments = metadata.classification_assignments || [];
    const clearUnassigned = metadata.clear_unassigned !== false;
    const classSyncSnapshot =
      chatService.projectContext?.current_project?.tasks ||
      window.allProjectTasks ||
      null;

    safeAddMessage(`Applying classification labels to ${classificationField}...`, "system");

    msProjectService.applyTextFieldLabelsWithSnapshot({
      assignments,
      textField: classificationField,
      allTasksSnapshot: classSyncSnapshot,
      clearUnassigned,
    })
      .then((result) => {
        console.log("Classification sync completed:", result);

        if (result.writes_failed > 0) {
          sentryCaptureException(new Error("Classification sync partial success"), {
            fingerprint: ["frontend_classification_sync_partial_success", classificationField],
            tags: {
              subsystem: "taskpane_actions",
              feature: "chat",
            },
            extra: payloadSnapshot(
              {
                classification_field: classificationField,
                labels_expected: result.labels_expected,
                labels_found: result.labels_found,
                writes_attempted: result.writes_attempted,
                writes_succeeded: result.writes_succeeded,
                writes_failed: result.writes_failed,
                used_snapshot: result.used_snapshot,
              },
              "classification_sync_partial_success"
            ),
            level: "warning",
          });
          safeAddMessage(
            `Classification partially applied. ${result.labels_found} labels were processed for '${result.text_field}', but ${result.writes_failed} task updates failed.`,
            "system"
          );
          return;
        }

        safeAddMessage(
          `Classification applied. ${result.labels_found} tasks labeled in '${result.text_field}'. ${result.writes_set_label} labels written, ${result.writes_cleared} cleared, ${result.writes_skipped} unchanged.`,
          "system"
        );
      })
      .catch(err => {
        console.error("Failed to apply classification labels:", err);
        sentryCaptureException(err, {
          fingerprint: ["frontend_user_action_failure", "classification_apply_failure"],
          tags: {
            subsystem: "taskpane_actions",
            feature: "chat",
          },
          extra: payloadSnapshot(
            {
              classification_field: classificationField,
              assignment_count: assignments.length,
            },
            "classification_apply_failure"
          ),
          level: "warning",
        });
        safeAddMessage("Error applying classification labels: " + err.message, "bot");
      });

    return;
  }

  // Handle Filtering / View Updates
  if (metadata.gantt_data && metadata.gantt_data.filtered) {
    console.log("Received Filtered View Data");
    safeAddMessage("Applying filter to MS Project view...", "system");

    const sourceFlagField = normalizeFlagField(metadata.flag_field) || "Flag10";
    const targetFlagField = normalizeFlagField(pendingFilterSyncPrefetch?.planned_flag_field);
    console.log(`Using backend filter marker field: ${sourceFlagField}`);
    console.log(`Using MS Project target flag field: ${targetFlagField}`);

    if (!targetFlagField || pendingFilterSyncPrefetch?.exhausted) {
      chatService.clearPendingFilterSyncPrefetch();
      sentryCaptureException(new Error("No filter flags available this session"), {
        fingerprint: ["frontend_filter_sync_exhausted"],
        tags: {
          subsystem: "taskpane_actions",
          feature: "chat",
        },
        extra: payloadSnapshot(
          {
            source_flag_field: sourceFlagField,
            planned_flag_field: pendingFilterSyncPrefetch?.planned_flag_field || null,
            next_filter_flag_index: nextFilterFlagIndex,
          },
          "filter_sync_exhausted"
        ),
        level: "warning",
      });
      safeAddMessage(
        "No session-managed filter flag columns are available. Reload the add-in or switch documents before applying another filter.",
        "system"
      );
      return;
    }

    // Extract tasks that have the backend source flag set to Yes/true.
    const allTasks = metadata.gantt_data.tasks || [];
    const flaggedTasks = allTasks.filter(t => {
      const lowerSourceField = sourceFlagField.toLowerCase();
      const customValue =
        (t.custom_fields && t.custom_fields[sourceFlagField]) ||
        (t.custom_fields && t.custom_fields[lowerSourceField]);
      const directValue = t[sourceFlagField] ?? t[lowerSourceField];

      return isAffirmativeFlagValue(customValue) || isAffirmativeFlagValue(directValue);
    });

    console.log(`Found ${flaggedTasks.length} matching tasks to flag with ${targetFlagField}.`);

    const filterSyncSnapshot =
      chatService.projectContext?.current_project?.tasks ||
      window.allProjectTasks ||
      null;

    // Apply to MS Project
    msProjectService.applyFilterFlags({
      filteredTasks: flaggedTasks,
      targetFlagField,
      sourceFlagField,
      plannedFlagField: targetFlagField,
      allTasksSnapshot: filterSyncSnapshot,
      prefetchedFlagState: pendingFilterSyncPrefetch,
    })
      .then((result) => {
        console.log("Filter sync completed:", result);

        if (!result.used_snapshot) {
          console.warn(
            `Filter sync used live Office.js fallback (${result.fallback_reason || "unknown_reason"}).`
          );
          sentryCaptureException(new Error("Filter sync fell back to live task fetch"), {
            fingerprint: ["frontend_filter_sync_fallback", result.fallback_reason || "unknown_reason"],
            tags: {
              subsystem: "taskpane_actions",
              feature: "chat",
            },
            extra: payloadSnapshot(
              {
                source_flag_field: sourceFlagField,
                target_flag_field: targetFlagField,
                planned_flag_field: result.planned_flag_field || targetFlagField,
                fallback_reason: result.fallback_reason || null,
                flagged_task_count: flaggedTasks.length,
                total_tasks_considered: result.total_tasks_considered,
              },
              "filter_sync_fallback"
            ),
            level: "warning",
          });
        }

        if (result.prefetch_fallback_used) {
          console.warn(
            `Filter sync prefetched flag state fallback used (${result.prefetch_fallback_reason || "unknown_reason"}).`
          );
          sentryCaptureException(new Error("Filter sync prefetched flag state fallback used"), {
            fingerprint: [
              "frontend_filter_sync_prefetch_fallback",
              result.prefetch_fallback_reason || "unknown_reason",
            ],
            tags: {
              subsystem: "taskpane_actions",
              feature: "chat",
            },
            extra: payloadSnapshot(
              {
                source_flag_field: sourceFlagField,
                target_flag_field: targetFlagField,
                planned_flag_field: result.planned_flag_field || targetFlagField,
                prefetch_fallback_reason: result.prefetch_fallback_reason || null,
                flagged_task_count: flaggedTasks.length,
                total_tasks_considered: result.total_tasks_considered,
              },
              "filter_sync_prefetch_fallback"
            ),
            level: "warning",
          });
        }

        if (result.writes_failed > 0) {
          chatService.clearPendingFilterSyncPrefetch();
          sentryCaptureException(new Error("Filter sync partial success"), {
            fingerprint: ["frontend_filter_sync_partial_success", targetFlagField],
            tags: {
              subsystem: "taskpane_actions",
              feature: "chat",
            },
            extra: payloadSnapshot(
              {
                source_flag_field: sourceFlagField,
                target_flag_field: targetFlagField,
                planned_flag_field: result.planned_flag_field || targetFlagField,
                flagged_task_count: flaggedTasks.length,
                matched_tasks_found: result.matched_tasks_found,
                current_yes_count: result.current_yes_count,
                desired_yes_count: result.desired_yes_count,
                writes_set_yes: result.writes_set_yes,
                writes_set_no: result.writes_set_no,
                writes_skipped: result.writes_skipped,
                writes_attempted: result.writes_attempted,
                writes_succeeded: result.writes_succeeded,
                writes_failed: result.writes_failed,
                used_snapshot: result.used_snapshot,
              },
              "filter_sync_partial_success"
            ),
            level: "warning",
          });

          safeAddMessage(
            `Filter partially applied. ${result.matched_tasks_found} matching tasks were processed for '${targetFlagField}', but ${result.writes_failed} task updates failed.`,
            "system"
          );
          return;
        }

        usedFilterFlagFields.add(targetFlagField);
        const appliedFlagNumber = Number(targetFlagField.replace("Flag", ""));
        if (!Number.isNaN(appliedFlagNumber)) {
          nextFilterFlagIndex = Math.max(nextFilterFlagIndex, appliedFlagNumber + 1);
        }
        chatService.clearPendingFilterSyncPrefetch();
        safeAddMessage(
          `Filter applied. ${result.matched_tasks_found} tasks marked with '${targetFlagField}'. ${result.writes_set_yes} set to Yes, ${result.writes_set_no} cleared, ${result.writes_skipped} unchanged.`,
          "system"
        );
      })
      .catch(err => {
        console.error("Failed to apply filter:", err);
        chatService.clearPendingFilterSyncPrefetch();
        sentryCaptureException(err, {
          fingerprint: ["frontend_user_action_failure", "filter_apply_failure"],
          tags: {
            subsystem: "taskpane_actions",
            feature: "chat",
          },
          extra: payloadSnapshot(
            {
              source_flag_field: sourceFlagField,
              target_flag_field: targetFlagField,
              planned_flag_field: targetFlagField,
              flagged_task_count: flaggedTasks.length,
            },
            "filter_apply_failure"
          ),
          level: "warning",
        });
        safeAddMessage("Error applying filter flags: " + err.message, "bot");
      });

    return;
  }

  if (route === "filtering") {
    chatService.clearPendingFilterSyncPrefetch();
    return;
  }

  chatService.clearPendingFilterSyncPrefetch();

  // Handle Live Edit Updates
  if (metadata.is_live_edit && metadata.live_updates) {
    console.log("Received Live Edit Updates:", metadata.live_updates);
    const updates = metadata.live_updates;

    if (updates.length > 0) {
      safeAddMessage(`Applying ${updates.length} update(s) to MS Project...`, "system");

      msProjectService.applyLiveUpdates(updates)
        .then((successCount) => {
          safeAddMessage(`✓ Applied ${successCount} update(s) successfully.`, "system");
        })
        .catch(err => {
          console.error("Failed to apply live updates:", err);
          sentryCaptureException(err, {
            fingerprint: ["frontend_user_action_failure", "live_edit_apply_failure"],
            tags: {
              subsystem: "taskpane_actions",
              feature: "live_edit",
            },
            extra: payloadSnapshot(
              {
                update_count: updates.length,
              },
              "live_edit_apply_failure"
            ),
            level: "warning",
          });
          safeAddMessage("Error applying updates: " + err.message, "bot");
        });
    }
  }
}

function displayTokenUsage(tokenUsage) {
  console.log("Token usage:", tokenUsage);

  const chatMessages = document.getElementById("chatMessages");
  const lastMessage = chatMessages.lastElementChild;

  if (lastMessage && lastMessage.classList.contains("bot-message")) {
    const messageContent = lastMessage.querySelector(".message-content");
    if (messageContent) {
      const tokenInfo = document.createElement("div");
      tokenInfo.className = "token-usage-info";
      tokenInfo.innerHTML = `
        <div class="token-stats">
          <span class="token-stat">
            <strong>Input:</strong> ${tokenUsage.input_tokens} tokens
          </span>
          <span class="token-stat">
            <strong>Output:</strong> ${tokenUsage.output_tokens} tokens
          </span>
          <span class="token-stat">
            <strong>Total:</strong> ${tokenUsage.total_tokens} tokens
          </span>
          ${tokenUsage.cost_estimate ? `
            <span class="token-stat">
              <strong>Cost:</strong> $${tokenUsage.cost_estimate.toFixed(4)}
            </span>
          ` : ''}
        </div>
      `;
      messageContent.appendChild(tokenInfo);
    }
  }
}



export {
  addMessage,
  sendMessage,
  displayProjectInfo,
  handleResponseMetadata,
  displayTokenUsage,
  msProjectService,
  viewManagementService
};
